import React, { useState, useEffect, useRef } from 'react';
import { devLog, devWarn } from '../utils/devLog';
import { workflowService } from '../services/workflowService';
import { jobsService } from '../services/jobsService';
import FileUploader, { UploadedFile } from './FileUploader';
import PastDocumentSelector from './PastDocumentSelector';
import DocumentCombination from './DocumentCombination';
import DocumentIntelligenceChip from './DocumentIntelligenceChip';
import DuplicateUploadBanner from './DuplicateUploadBanner';
import SparkleIcon from './icons/SparkleIcon';
import { DuplicateUploadNotice } from '../hooks/useProjectFileDrop';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithActivity } from '../utils/fetchWithActivity';
import { categorizeError, CategorizedError } from '../utils/errorUtils';
import { formatWorkflowLabel } from '../constants/betaToolLabels';

// E9 preflight hint copy — only where the generic "needs at least N
// documents" line would be unclear about *which* documents to pick.
const DOC_COUNT_HINT_COPY: Record<string, string> = {
  year_over_year: "This workflow compares policy terms — select this year's and last year's documents.",
  yoy_renewal: "This workflow compares policy terms — select this year's and last year's documents.",
  personal_lines_yoy_renewal: "This workflow compares policy terms — select this year's and last year's documents.",
  document_comparison: 'Select at least two like documents to compare side by side.',
  policy_comparison: 'Select at least two like documents to compare side by side.',
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Standardized file types across all workflows
const STANDARD_ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'text/csv', // .csv
  'message/rfc822', // .eml
  'application/vnd.ms-outlook' // .msg
];

// Tool Execution Component
interface ToolExecutionProps {
  tool: 'research_tool' | 'loss_run_tool';
  clientId: string;
  projectId?: string;
  onComplete: (result: any) => void;
  loading: boolean;
}

export const ToolExecution: React.FC<ToolExecutionProps> = ({ tool, clientId, projectId, onComplete, loading }) => {
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleExecute = async () => {
    try {
      setIsExecuting(true);
      let result;
      
      if (tool === 'research_tool') {
        result = await workflowService.executeResearchTool(clientId, projectId);
      } else {
        result = await workflowService.executeLossRunTool(clientId, projectId);
      }
      
      setResult(result);
      onComplete(result);
    } catch (error) {
      console.error('Error executing tool:', error);
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
        Execute {tool === 'research_tool' ? 'Research Tool' : 'Loss Run Tool'}
      </h3>
      
      {!result && (
        <div className="text-center">
          <button
            onClick={handleExecute}
            disabled={isExecuting}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
          >
            {isExecuting ? 'Executing...' : `Run ${tool === 'research_tool' ? 'Research' : 'Loss Run'} Analysis`}
          </button>
        </div>
      )}
      
      {result && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
            {tool === 'research_tool' ? 'Research' : 'Loss Run'} Analysis Complete
          </h4>
          <p className="text-sm text-green-700 dark:text-green-300">
            {result.summary || 'Analysis completed successfully'}
          </p>
          
          {result.documents && result.documents.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                Documents processed: {result.documents.length}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Save Point Component
interface SavePointProps {
  onSave: () => void;
  onContinue: () => void;
  canSave: boolean;
}

export const SavePoint: React.FC<SavePointProps> = ({ onSave, onContinue, canSave }) => {
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    try {
      await onSave();
      setSaved(true);
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Save Progress</h3>
      <p className="text-gray-600 dark:text-gray-400">
        You can save your progress here and continue later, or proceed to the next step.
      </p>
      
      <div className="flex gap-4">
        <button
          onClick={handleSave}
          disabled={!canSave || saved}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saved ? 'Saved ✓' : 'Save Progress'}
        </button>
        
        <button
          onClick={onContinue}
          className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

// Policy Comparison Component
interface PolicyComparisonProps {
  researchReport: any;
  clientId: string;
  projectId?: string;
  onComplete: (result: any) => void;
  loading: boolean;
}

export const PolicyComparison: React.FC<PolicyComparisonProps> = ({ 
  researchReport, 
  clientId, 
  projectId,
  onComplete, 
  loading 
}) => {
  const [isComparing, setIsComparing] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleCompare = async () => {
    try {
      setIsComparing(true);
      // Mock comparison logic - in real implementation, this would call an API
      const comparisonResult = {
        id: Date.now().toString(),
        content: 'Policy comparison completed',
        differences: [],
        recommendations: []
      };
      
      setResult(comparisonResult);
      onComplete(comparisonResult);
    } catch (error) {
      console.error('Error comparing policies:', error);
    } finally {
      setIsComparing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Policy Comparison</h3>
      
      {!result && (
        <div className="text-center">
          <button
            onClick={handleCompare}
            disabled={isComparing}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
          >
            {isComparing ? 'Comparing...' : 'Compare with Existing Policies'}
          </button>
        </div>
      )}
      
      {result && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
            Policy Comparison Complete
          </h4>
          <p className="text-sm text-green-700 dark:text-green-300">
            Comparison analysis has been completed successfully.
          </p>
        </div>
      )}
    </div>
  );
};

// Previous Application Upload Component
interface PreviousApplicationUploadProps {
  onUpload: (files: File[]) => void;
  clientId?: string;
  projectId?: string;
}

export const PreviousApplicationUpload: React.FC<PreviousApplicationUploadProps> = ({ onUpload, clientId, projectId }) => {
  const [files, setFiles] = useState<File[]>([]);

  const handleFilesUploaded = (uploadedFiles: UploadedFile[]) => {
    const fileObjects = uploadedFiles.map(uf => uf.file).filter((file): file is File => !!file);
    setFiles(fileObjects);
  };

  const handleUpload = () => {
    if (files.length > 0) {
      onUpload(files);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Upload Previous Application</h3>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Upload the previous year's application documents for comparison
      </p>
      
      <div className="space-y-4">
        <FileUploader
          onFilesUploaded={handleFilesUploaded}
          maxFiles={5}
          customerId={clientId}
          projectId={projectId}
          hideIndividualSuccessMessages={true}
          acceptedTypes={STANDARD_ACCEPTED_TYPES}
        />
        
        <button
          onClick={handleUpload}
          disabled={files.length === 0}
          className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50"
        >
          Upload and Continue
        </button>
      </div>
    </div>
  );
};

// New Application Upload Component
interface NewApplicationUploadProps {
  onUpload: (files: File[], context?: string) => void;
  clientId?: string;
  projectId?: string;
}

export const NewApplicationUpload: React.FC<NewApplicationUploadProps> = ({ onUpload, clientId, projectId }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [context, setContext] = useState('');

  const handleFilesUploaded = (uploadedFiles: UploadedFile[]) => {
    const fileObjects = uploadedFiles.map(uf => uf.file).filter((file): file is File => !!file);
    setFiles(fileObjects);
  };

  const handleUpload = () => {
    if (files.length > 0) {
      onUpload(files, context);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Upload New Application</h3>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Upload the current renewal application documents
      </p>
      
      <div className="space-y-4">
        <FileUploader
          onFilesUploaded={handleFilesUploaded}
          maxFiles={5}
          customerId={clientId}
          projectId={projectId}
          hideIndividualSuccessMessages={true}
          acceptedTypes={STANDARD_ACCEPTED_TYPES}
        />
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Additional Context (Optional)
          </label>
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Enter any specific areas to focus on during comparison..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
            rows={3}
          />
        </div>
        
        <button
          onClick={handleUpload}
          disabled={files.length === 0}
          className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50"
        >
          Upload and Proceed
        </button>
      </div>
    </div>
  );
};

// Application Upload Component (legacy - kept for backward compatibility)
interface ApplicationUploadProps {
  onUpload: (files: File[], context?: string) => void;
  clientId?: string;
  projectId?: string;
}

export const ApplicationUpload: React.FC<ApplicationUploadProps> = ({ onUpload, clientId, projectId }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [context, setContext] = useState('');

  const handleFilesUploaded = (uploadedFiles: UploadedFile[]) => {
    const fileObjects = uploadedFiles.map(uf => uf.file).filter((file): file is File => !!file);
    setFiles(fileObjects);
  };

  const handleUpload = () => {
    if (files.length > 0) {
      onUpload(files, context);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Upload Applications</h3>
      
      <div className="space-y-4">
        <FileUploader
          onFilesUploaded={handleFilesUploaded}
          customerId={clientId}
          projectId={projectId}
          hideIndividualSuccessMessages={true}
          acceptedTypes={STANDARD_ACCEPTED_TYPES}
        />
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Additional Context (Optional)
          </label>
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Enter any additional context..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
            rows={3}
          />
        </div>
        
        <button
          onClick={handleUpload}
          disabled={files.length === 0}
          className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50"
        >
          Upload and Proceed
        </button>
      </div>
    </div>
  );
};

// Application Comparison Component
interface ApplicationComparisonProps {
  previousApplications?: File[];
  newApplications?: File[];
  applications?: File[]; // For backward compatibility
  context?: string;
  clientId: string;
  projectId?: string;
  onComplete: (result: any) => void;
  loading: boolean;
}

export const ApplicationComparison: React.FC<ApplicationComparisonProps> = ({
  previousApplications,
  newApplications,
  applications,
  context,
  clientId,
  projectId,
  onComplete,
  loading
}) => {
  const [isComparing, setIsComparing] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleCompare = async () => {
    try {
      setIsComparing(true);
      
      // Determine which files to use for comparison
      const newFiles = newApplications || applications || [];
      const prevFiles = previousApplications || [];
      
      const comparisonResult = await workflowService.compareApplications({
        new: newFiles,
        previous: prevFiles,
        context,
        projectId
      });
      
      setResult(comparisonResult);
      onComplete(comparisonResult);
    } catch (error) {
      console.error('Error comparing applications:', error);
    } finally {
      setIsComparing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Application Comparison</h3>
      
      {!result && (
        <div className="text-center">
          <button
            onClick={handleCompare}
            disabled={isComparing}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
          >
            {isComparing ? 'Comparing...' : 'Compare Applications'}
          </button>
        </div>
      )}
      
      {result && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
            Application Comparison Complete
          </h4>
          <p className="text-sm text-green-700 dark:text-green-300">
            {result.summary || 'Application comparison completed successfully'}
          </p>
        </div>
      )}
    </div>
  );
};

// Save/Export Point Component
interface SaveExportPointProps {
  report: any;
  onSave: () => void;
  onExport: () => void;
  onContinue: () => void;
}

export const SaveExportPoint: React.FC<SaveExportPointProps> = ({
  report,
  onSave,
  onExport,
  onContinue
}) => {
  const [saved, setSaved] = useState(false);
  const [exported, setExported] = useState(false);

  const handleSave = async () => {
    try {
      await onSave();
      setSaved(true);
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  const handleExport = async () => {
    try {
      await onExport();
      setExported(true);
    } catch (error) {
      console.error('Error exporting:', error);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Save/Export Report</h3>
      <p className="text-gray-600 dark:text-gray-400">
        Save your report, export it, or continue to the next step.
      </p>
      
      <div className="flex gap-4 flex-wrap">
        <button
          onClick={handleSave}
          disabled={saved}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saved ? 'Saved ✓' : 'Save Report'}
        </button>
        
        <button
          onClick={handleExport}
          disabled={exported}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
        >
          {exported ? 'Exported ✓' : 'Export PDF'}
        </button>
        
        <button
          onClick={onContinue}
          className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

// Research Tool Component
interface ResearchToolProps {
  clientId: string;
  projectId?: string;
  renewalContext?: any;
  onComplete: (result: any) => void;
  loading: boolean;
}

export const ResearchTool: React.FC<ResearchToolProps> = ({
  clientId,
  projectId,
  renewalContext,
  onComplete,
  loading
}) => {
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [companyUrl, setCompanyUrl] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');

  const handleExecute = async () => {
    if (!companyUrl.trim()) {
      setError('Please enter a company URL to research');
      return;
    }

    try {
      setIsExecuting(true);
      setError(null);
      
      // First try the workflow service, but fall back to direct research API call
      let researchResult;
      try {
        researchResult = await workflowService.executeResearchTool(clientId, projectId, {
          renewalContext: true,
          previousComparison: renewalContext,
          companyUrl,
          additionalContext
        });
      } catch (serviceError) {
        console.warn('Workflow service failed, calling research API directly:', serviceError);
        
        // Call the research API directly as a fallback
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
        const response = await fetch(`${API_BASE_URL}/api/research/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
          },
          body: JSON.stringify({
            url: companyUrl,
            additionalText: additionalContext || `Research for client ID: ${clientId}. ${renewalContext ? 'This is for a renewal context.' : ''}`,
            documents: [],
            customerContext: clientId,
            projectId: projectId
          })
        });

        if (!response.ok) {
          throw new Error('Failed to generate research report');
        }

        const data = await response.json();
        researchResult = {
          id: data.id || Date.now().toString(),
          summary: `Research completed for ${companyUrl}`,
          content: data.content || data.report || 'Research analysis completed',
          companyUrl,
          additionalContext,
          timestamp: new Date()
        };
      }
      
      setResult(researchResult);
      onComplete(researchResult);
    } catch (error) {
      console.error('Error executing research tool:', error);
      setError('Failed to complete research analysis. Please try again.');
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Research Tool</h3>
      <p className="text-gray-600 dark:text-gray-400">
        Research company information, risk factors, and market intelligence
      </p>
      
      {error && (
        <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded">
          {error}
        </div>
      )}
      
      {isExecuting && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
            <div>
              <h4 className="font-medium text-orange-800 dark:text-orange-200 mb-1">
                Researching Company Information
              </h4>
              <p className="text-sm text-orange-700 dark:text-orange-300">
                Analyzing {companyUrl} and gathering market intelligence...
              </p>
            </div>
          </div>
          <div className="mt-4 bg-orange-200 dark:bg-orange-800 rounded-full h-2">
            <div className="bg-orange-500 h-2 rounded-full animate-pulse" style={{ width: '70%' }}></div>
          </div>
        </div>
      )}

      {!result && !isExecuting && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Company URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              value={companyUrl}
              onChange={(e) => setCompanyUrl(e.target.value)}
              placeholder="https://example-company.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Additional Context (Optional)
            </label>
            <textarea
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="Enter any specific research focus areas or additional context..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              rows={3}
            />
          </div>
          
          <div className="text-center">
            <button
              onClick={handleExecute}
              disabled={isExecuting || !companyUrl.trim()}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
            >
              Execute Research Tool
            </button>
          </div>
        </div>
      )}
      
      
      {result && (
        <div className="space-y-4">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
              Research Complete
            </h4>
            <p className="text-sm text-green-700 dark:text-green-300">
              {result.summary || 'Research analysis completed successfully'}
            </p>
            <div className="mt-2 text-xs text-green-600 dark:text-green-400">
              <p>Company: {result.companyUrl}</p>
              {result.timestamp && <p>Completed: {new Date(result.timestamp).toLocaleString()}</p>}
            </div>
          </div>
          
          {result.content && (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h5 className="font-medium text-gray-900 dark:text-white mb-2">Research Report</h5>
              <div className="prose dark:prose-invert prose-sm max-w-none">
                <div className="whitespace-pre-wrap text-sm">{result.content}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Comparison Type Selection Component
interface ComparisonTypeSelectionProps {
  onSelect: (type: string) => void;
  selectedType?: string;
}

export const ComparisonTypeSelection: React.FC<ComparisonTypeSelectionProps> = ({ onSelect, selectedType }) => {
  devLog('🔍 ComparisonTypeSelection rendered with selectedType:', selectedType);
  
  const comparisonTypes = [
    { id: 'quote_quote', label: 'Quote Comparison', description: 'Compare multiple quotes' },
    { id: 'policy_policy', label: 'Policy Comparison', description: 'Compare policy documents' },
    { id: 'quote_binder_policy', label: 'Quote/Binder vs Policy', description: 'Compare quote or binder with policy' },
    { id: 'binder_quote', label: 'Binder vs Quote', description: 'Compare binder with quote' }
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Select Comparison Type</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {comparisonTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => {
              devLog('🔄 User clicked:', type.id, 'Current selectedType:', selectedType);
              onSelect(type.id);
            }}
            className={`p-4 border rounded-lg text-left transition-colors ${
              selectedType === type.id
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400 ring-2 ring-blue-200 dark:ring-blue-800'
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className={`font-medium ${
                  selectedType === type.id 
                    ? 'text-blue-900 dark:text-blue-100' 
                    : 'text-gray-900 dark:text-white'
                }`}>
                  {type.label}
                </h4>
                <p className={`text-sm mt-1 ${
                  selectedType === type.id 
                    ? 'text-blue-700 dark:text-blue-300' 
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {type.description}
                </p>
              </div>
              {selectedType === type.id && (
                <div className="ml-3">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-blue-600 dark:text-blue-400">
                    <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.41,10.09L6,11.5L11,16.5Z" />
                  </svg>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
      
      {selectedType && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-green-600">
              <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.41,10.09L6,11.5L11,16.5Z" />
            </svg>
            <span className="font-medium text-green-900 dark:text-green-100">
              {comparisonTypes.find(t => t.id === selectedType)?.label} selected
            </span>
          </div>
          <p className="text-sm text-green-700 dark:text-green-300 mt-1">
            Ready to proceed to document selection.
          </p>
        </div>
      )}
    </div>
  );
};


// Placeholder components for tools
export const QuoteComparisonTool: React.FC<{ context: any; clientId: string; projectId?: string }> = ({ context, clientId, projectId }) => (
  <div className="text-center py-8">
    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Quote Comparison Tool</h3>
    <p className="text-gray-600 dark:text-gray-400">
      Integration with Quote Comparison Tool would be implemented here
    </p>
    <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
      <p className="text-sm">Context: {JSON.stringify(context, null, 2)}</p>
    </div>
  </div>
);

export const YearOverYearTool: React.FC<{ mode: string; context: any; clientId: string; projectId?: string }> = ({ mode, context, clientId, projectId }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      setError(null);
      
      // Get documents from context for year-over-year analysis
      const documentsForAnalysis = [];
      
      // Add previous year documents if available
      if (context.previousApplications) {
        documentsForAnalysis.push(...context.previousApplications.map((doc: any) => ({
          ...doc,
          period: 'previous'
        })));
      }
      
      // Add current/new documents if available
      if (context.newApplications) {
        documentsForAnalysis.push(...context.newApplications.map((doc: any) => ({
          ...doc,
          period: 'current'
        })));
      }
      
      // Add any other documents from comparison reports
      if (context.comparisonReport?.documents) {
        documentsForAnalysis.push(...context.comparisonReport.documents);
      }
      
      if (documentsForAnalysis.length < 2) {
        setError('At least 2 documents are required for year-over-year analysis');
        return;
      }
      
      // Use workflow service for year-over-year analysis
      const analysisResult = await workflowService.executeYearOverYearAnalysis({
        documents: documentsForAnalysis,
        mode: mode,
        clientId: clientId,
        projectId: projectId,
        context: context
      });
      
      setResult(analysisResult);
    } catch (error) {
      console.error('Error analyzing year over year:', error);
      setError(error instanceof Error ? error.message : 'Failed to complete year over year analysis');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Year Over Year Analysis</h3>
      <p className="text-gray-600 dark:text-gray-400">
        Analyze changes and trends compared to the previous policy period
      </p>
      
      {error && (
        <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded">
          {error}
        </div>
      )}
      
      {!result && !error && (
        <div className="text-center">
          <button
            onClick={handleAnalyze}
            disabled={isAnalyzing}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
          >
            {isAnalyzing ? 'Analyzing Changes...' : 'Run Year Over Year Analysis'}
          </button>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Mode: {mode} | Client: {clientId}
          </p>
        </div>
      )}
      
      {result && (
        <div className="space-y-4">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
              Analysis Complete
            </h4>
            <p className="text-sm text-green-700 dark:text-green-300">
              {result.summary}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {result.trends?.map((trend: any, index: number) => (
              <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 dark:text-white">{trend.category}</h5>
                <p className="text-lg font-semibold text-primary mt-1">{trend.change}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{trend.details}</p>
              </div>
            ))}
          </div>
          
          {result.recommendations && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h5 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Recommendations</h5>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                {result.recommendations.map((rec: string, index: number) => (
                  <li key={index} className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const DocumentComparison: React.FC<{
  documents: any[];
  comparisonType: string;
  onComplete: (result: any) => void;
  loading: boolean;
  clientId?: string;
  projectId?: string;
}> = ({ documents, comparisonType, onComplete, loading, clientId, projectId }) => {
  const [isComparing, setIsComparing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Initializing...');

  // Listen for progress updates
  useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      const { progress: newProgress, status } = event.detail;
      devLog('📊 Document Comparison Progress update received:', { progress: newProgress, status });
      setProgress(newProgress);
      setProgressMessage(getProgressMessage(newProgress));
    };

    window.addEventListener('documentComparisonProgress' as any, handleProgressUpdate);

    return () => {
      window.removeEventListener('documentComparisonProgress' as any, handleProgressUpdate);
    };
  }, []);

  const getProgressMessage = (progress: number): string => {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading documents...';
    if (progress < 30) return 'Preparing comparison...';
    if (progress < 40) return 'Generating comparison prompt...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 70) return 'AI comparing documents... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations...';
    if (progress < 90) return 'Saving comparison report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  };

  // Auto-execute comparison when component mounts and has documents
  useEffect(() => {
    if (documents && documents.length >= 2 && !result && !isComparing && !error) {
      handleCompare();
    }
  }, [documents, result, isComparing, error]);

  const handleCompare = async () => {
    try {
      setIsComparing(true);
      setError(null);
      setProgress(0);
      setProgressMessage('Starting comparison...');

      const comparisonResult = await workflowService.runDocumentComparison({
        documents,
        type: comparisonType,
        customerId: clientId,
        projectId: projectId
      });

      setResult(comparisonResult);
      onComplete(comparisonResult);
    } catch (error) {
      console.error('Error comparing documents:', error);
      setError(error instanceof Error ? error.message : 'Failed to compare documents');
    } finally {
      setIsComparing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Document Comparison</h3>
      
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">
            Comparison Failed
          </h4>
          <p className="text-sm text-red-700 dark:text-red-300">
            {error}
          </p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
          >
            Try Again
          </button>
        </div>
      )}
      
      {!result && !isComparing && !error && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-green-600">
              <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.41,10.09L6,11.5L11,16.5Z" />
            </svg>
            <span className="font-medium text-green-900 dark:text-green-100">
              Ready to start comparison
            </span>
          </div>
          <p className="text-sm text-green-700 dark:text-green-300 mt-1">
            {documents.length} documents selected for {comparisonType.replace('_', ' ')} comparison. Analysis will begin automatically.
          </p>
        </div>
      )}

      {isComparing && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
          <div className="flex items-center space-x-4 mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <div className="flex-1">
              <h4 className="font-medium text-green-800 dark:text-green-200 mb-1">
                Comparing Documents
              </h4>
              <p className="text-sm text-green-700 dark:text-green-300">
                {progressMessage}
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {progress}%
              </div>
            </div>
          </div>

          {/* Real-time Progress Bar */}
          <div className="bg-green-200 dark:bg-green-800 rounded-full h-3 overflow-hidden">
            <div
              className="bg-green-500 dark:bg-green-400 h-3 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          <p className="text-xs text-green-600 dark:text-green-400 mt-2 text-center">
            Analyzing {documents.length} document{documents.length !== 1 ? 's' : ''} with AI-powered comparison...
          </p>
        </div>
      )}
      
      {result && (
        <div className="space-y-4">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
              Document Comparison Complete
            </h4>
            <p className="text-sm text-green-700 dark:text-green-300">
              {result.summary || 'Document comparison completed successfully'}
            </p>
            {result.citationsEnabled === false && (
              <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2">
                ⚠️ Citation highlighting not available - documents need span extraction
              </p>
            )}
          </div>
          
          {/* Report completion message - actual report opens in full page */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h5 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
              📄 Report Generated Successfully
            </h5>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Your comparison report has been generated and will open in a new page with full features including citations, export options, and integrated chat.
            </p>
            {result.citationsEnabled && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                ✅ Citations are available for fact verification
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const DocumentSourceSelection: React.FC<{
  clientId: string;
  projectId?: string;
  onSelect: (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => void;
  hideUploadSection?: boolean;
  workflowType?: string;
  initialPastDocumentIds?: string[];
  suggestionsLoading?: boolean;
}> = ({ clientId, projectId, onSelect, hideUploadSection = false, workflowType, initialPastDocumentIds, suggestionsLoading = false }) => {
  const [selectedDocuments, setSelectedDocuments] = useState<any[]>([]);
  const [documentsReady, setDocumentsReady] = useState(false);
  const [documentStatuses, setDocumentStatuses] = useState<{[key: string]: 'checking' | 'processing' | 'completed' | 'error'}>({});
  const [selectedPastDocuments, setSelectedPastDocuments] = useState<any[]>([]);
  const [selectedPastReports, setSelectedPastReports] = useState<any[]>([]);
  const [showAutoSelectedBanner, setShowAutoSelectedBanner] = useState(false);
  const [autoSelectedCount, setAutoSelectedCount] = useState(0);
  const [duplicateNotice, setDuplicateNotice] = useState<DuplicateUploadNotice | null>(null);
  const { token } = useAuth();
  // Guards against the async suggestion fetch (WorkflowModal) landing after the
  // user has already made their own past-document selection — a real click
  // must never be silently overwritten by a late-arriving suggestion.
  const userInteractedRef = useRef(false);

  // E9 — preflight document-count hint. minDocuments comes from the
  // workflow-categories payload (sourced from WorkflowRegistry server-side).
  const [minDocsRequired, setMinDocsRequired] = useState<number | null>(null);
  useEffect(() => {
    if (!workflowType || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithActivity(`${API_BASE_URL}/api/workflows/categories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const allTools = [
          ...((data.categories || []).flatMap((c: any) => c.tools || [])),
          ...(data.uncategorizedTools || []),
        ];
        const match = allTools.find((t: any) => t.workflowType === workflowType);
        if (!cancelled && match?.minDocuments) setMinDocsRequired(match.minDocuments);
      } catch {
        // Non-critical — the hint just won't render without a known min.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workflowType, token]);

  // Get workflow-specific text
  const getDocumentText = () => {
    switch (workflowType) {
      case 'renewal':
        return {
          title: 'Upload Renewal Documents',
          description: 'Upload the documents for this analysis — Risk Ninja automatically detects and combines related documents (like a policy and its endorsements). Everything is saved to your project folder.'
        };
      case 'loss_run':
        return {
          title: 'Upload Loss Run Documents',
          description: 'Upload the documents for this analysis — Risk Ninja automatically detects and combines related documents (like a policy and its endorsements). Everything is saved to your project folder.'
        };
      case 'year_over_year':
        return {
          title: 'Upload Year Over Year Documents',
          description: 'Upload the documents for this analysis — Risk Ninja automatically detects and combines related documents (like a policy and its endorsements). Everything is saved to your project folder.'
        };
      case 'certificate_of_effort':
        return {
          title: 'Upload Documents for Certificate of Effort',
          description: 'Upload the documents for this analysis — Risk Ninja automatically detects and combines related documents (like a policy and its endorsements). Everything is saved to your project folder.'
        };
      case 'coverage_analysis':
        return {
          title: 'Upload Policy Documents',
          description: 'Upload the documents for this analysis — Risk Ninja automatically detects and combines related documents (like a policy and its endorsements). Everything is saved to your project folder.'
        };
      case 'universal':
      case 'universal_analysis':
        return {
          title: 'Upload Documents for Analysis',
          description: 'Upload the documents for this analysis — Risk Ninja automatically detects and combines related documents (like a policy and its endorsements). Everything is saved to your project folder.'
        };
      case 'claims':
        return {
          title: 'Upload Documents for Claims Analysis',
          description: 'Upload the documents for this analysis — Risk Ninja automatically detects and combines related documents (like a policy and its endorsements). Everything is saved to your project folder.'
        };
      default:
        return {
          title: 'Upload Documents',
          description: 'Upload the documents for this analysis — Risk Ninja automatically detects and combines related documents (like a policy and its endorsements). Everything is saved to your project folder.'
        };
    }
  };

  const documentText = getDocumentText();

  // Update parent with all selected items (new uploads + past documents + past reports)
  const updateParentSelection = (
    newUploads: any[] = selectedDocuments, 
    pastDocs: any[] = selectedPastDocuments, 
    pastReports: any[] = selectedPastReports
  ) => {
    const combinedSelection = {
      uploadedDocuments: newUploads,
      pastDocuments: pastDocs,
      pastReports: pastReports,
      allDocuments: [...newUploads, ...pastDocs] // For backward compatibility with workflows that expect document arrays
    };
    onSelect(combinedSelection);
  };

  // Handle past documents selection (user-driven — a real click always wins)
  const handlePastDocumentsSelected = (documents: any[]) => {
    userInteractedRef.current = true;
    setSelectedPastDocuments(documents);
    updateParentSelection(selectedDocuments, documents, selectedPastReports);
  };

  // Handle past reports selection (user-driven — a real click always wins)
  const handlePastReportsSelected = (reports: any[]) => {
    userInteractedRef.current = true;
    setSelectedPastReports(reports);
    updateParentSelection(selectedDocuments, selectedPastDocuments, reports);
  };

  // Pre-select documents matched by a workflow suggestion ("Run" from the
  // Recommended cards, or a catalog launch's async suggestion lookup) —
  // fetch the project's documents once and select the matching ids via the
  // same state setters a user click would use, so parent state/canProceed
  // stay consistent.
  useEffect(() => {
    if (!initialPastDocumentIds || initialPastDocumentIds.length === 0 || !projectId) return;
    // A catalog launch's suggestion fetch can resolve after the user has
    // already picked documents by hand — never clobber a real selection.
    if (userInteractedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('riskninja-token');
        const res = await fetch(`${API_BASE_URL}/api/documents?projectId=${projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled || userInteractedRef.current) return;
        const docs = await res.json();
        const matched = docs.filter((d: any) => initialPastDocumentIds.includes(d.id));
        if (matched.length > 0 && !cancelled && !userInteractedRef.current) {
          setSelectedPastDocuments(matched);
          updateParentSelection(selectedDocuments, matched, selectedPastReports);
          setAutoSelectedCount(matched.length);
          setShowAutoSelectedBanner(true);
        }
      } catch {
        // Non-critical — the user can still select documents manually.
      }
    })();
    return () => { cancelled = true; };
    // initialPastDocumentIds can arrive after mount (catalog launches consult
    // the suggestion engine asynchronously in WorkflowModal) — react to it,
    // not just to projectId being present at mount time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, initialPastDocumentIds]);

  // Check processing status for a document
  const checkDocumentStatus = async (documentId: string) => {
    try {
      // Handle combined documents - they're always ready
      if (documentId.startsWith('combined_')) {
        setDocumentStatuses(prev => ({
          ...prev,
          [documentId]: 'completed'
        }));
        return 'completed';
      }

      const token = localStorage.getItem('riskninja-token');
      if (!token) return;

      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/documents/${documentId}/processing-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        
        let status: 'checking' | 'processing' | 'completed' | 'error' = 'checking';
        
        if (data.backgroundJob) {
          if (data.backgroundJob.status === 'completed') {
            status = 'completed';
          } else if (data.backgroundJob.status === 'error') {
            status = 'error';
          } else {
            status = 'processing';
          }
        } else if (data.documentStatus === 'completed') {
          status = 'completed';
        } else if (data.documentStatus === 'processing') {
          status = 'processing';
        }

        setDocumentStatuses(prev => ({
          ...prev,
          [documentId]: status
        }));

        return status;
      }
    } catch (error) {
      console.error('Error checking document status:', documentId, error);
      setDocumentStatuses(prev => ({
        ...prev,
        [documentId]: 'error'
      }));
    }
  };

  // Update documents ready state based on all document statuses
  useEffect(() => {
    if (selectedDocuments.length === 0) {
      setDocumentsReady(false);
      return;
    }

    const allStatuses = selectedDocuments.map(doc => documentStatuses[doc.id] || 'checking');
    const allCompleted = allStatuses.every(status => status === 'completed');
    const hasProcessing = allStatuses.some(status => status === 'processing');
    
    setDocumentsReady(allCompleted && !hasProcessing);
  }, [selectedDocuments, documentStatuses]);

  const handleNewDocuments = (files: UploadedFile[]) => {
    devLog('🔄 DocumentSourceSelection: Handling uploaded files:', {
      filesCount: files.length,
      fileData: files.map(f => ({ 
        id: f.id, 
        name: f.name, 
        status: f.status, 
        hasRealId: !!f.id && typeof f.id === 'string' && f.id.includes('-')
      }))
    });

    const duplicate = files.find((f) => f.duplicateOfDocumentId);
    if (duplicate?.duplicateOfDocumentId) {
      setDuplicateNotice({
        message: `"${duplicate.name}" looks identical to a document already in this project.`,
        fileName: duplicate.name,
        uploadedDocumentId: duplicate.id,
        duplicateOfDocumentId: duplicate.duplicateOfDocumentId,
      });
    }

    // CRITICAL FIX: Use the actual document IDs from FileUploader, not generated timestamps
    const docs = files.map(f => ({
      id: f.id,           // Use the real UUID from the backend, not Date.now() + Math.random()
      name: f.name,
      file: f.file,
      status: f.status,
      type: f.type,
      size: f.size
    }));
    
    devLog('✅ DocumentSourceSelection: Prepared documents with real UUIDs:', {
      documentsCount: docs.length,
      documentIds: docs.map(d => d.id),
      documentNames: docs.map(d => d.name)
    });
    
    // APPEND new documents to existing selection instead of replacing
    setSelectedDocuments(prev => {
      // Filter out any duplicates based on document ID
      const existingIds = prev.map(d => d.id);
      const newDocs = docs.filter(d => !existingIds.includes(d.id));
      const updatedSelection = [...prev, ...newDocs];
      
      devLog('📚 Updated document selection:', {
        previousCount: prev.length,
        newDocsCount: newDocs.length,
        finalCount: updatedSelection.length,
        allDocumentNames: updatedSelection.map(d => d.name)
      });
      
      // Call updateParentSelection with the complete updated selection
      updateParentSelection(updatedSelection, selectedPastDocuments, selectedPastReports);
      return updatedSelection;
    });
    
    // Start checking processing status for newly uploaded documents
    docs.forEach(doc => {
      setDocumentStatuses(prev => ({
        ...prev,
        [doc.id]: 'checking'
      }));
      
      // Start polling for document status
      const pollStatus = async () => {
        const status = await checkDocumentStatus(doc.id);
        if (status === 'processing') {
          // Continue polling every 2 seconds if still processing
          setTimeout(pollStatus, 2000);
        }
      };
      
      pollStatus();
    });
  };

  return (
    <div className="space-y-4">
      {!hideUploadSection && (
        <>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">{documentText.title}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {documentText.description}
          </p>
        </>
      )}
      
      {/* Catalog launches consult the suggestion engine asynchronously — this
          skeleton covers the gap so the doc list doesn't look final before it is. */}
      {suggestionsLoading && !showAutoSelectedBanner && (
        <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg animate-pulse">
          <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm text-accent dark:text-dark-muted">Checking this project's documents…</span>
        </div>
      )}

      {/* Auto-selection announcement — the trust checkpoint: never auto-advance
          past this step, the user confirms with one click. */}
      {showAutoSelectedBanner && selectedPastDocuments.length > 0 && (
        <div className="flex items-start justify-between gap-3 p-3 bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/30 rounded-lg">
          <p className="text-sm text-secondary dark:text-dark-text flex items-start gap-1.5">
            <SparkleIcon className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <span>{autoSelectedCount} document{autoSelectedCount !== 1 ? 's' : ''} selected automatically based on Risk Ninja's analysis — review below or adjust.</span>
          </p>
          <button
            type="button"
            onClick={() => setShowAutoSelectedBanner(false)}
            className="flex-shrink-0 text-xs text-accent dark:text-dark-muted hover:text-secondary dark:hover:text-dark-text transition-colors"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Past Documents and Reports Selection */}
      <div className="flex justify-between items-start gap-4">
        <div className="flex-1">
          <PastDocumentSelector
            customerId={clientId}
            projectId={projectId}
            onDocumentsSelected={handlePastDocumentsSelected}
            onReportsSelected={handlePastReportsSelected}
            selectedDocuments={selectedPastDocuments}
            selectedReports={selectedPastReports}
          />
        </div>
      </div>
      
      {duplicateNotice && (
        <DuplicateUploadBanner
          notice={duplicateNotice}
          token={token}
          onDismiss={() => setDuplicateNotice(null)}
          onRemoved={() => {
            setSelectedDocuments((prev) => {
              const updated = prev.filter((d) => d.id !== duplicateNotice.uploadedDocumentId);
              updateParentSelection(updated, selectedPastDocuments, selectedPastReports);
              return updated;
            });
            setDuplicateNotice(null);
          }}
        />
      )}

      <div className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg">
        <FileUploader
          onFilesUploaded={handleNewDocuments}
          customerId={clientId}
          projectId={projectId}
          hideIndividualSuccessMessages={true}
          acceptedTypes={STANDARD_ACCEPTED_TYPES}
        />
      </div>

      {/* E9 — preflight document-count hint. Doesn't nag at zero (the empty
          state already explains); only once the user has started but hasn't
          met the minimum yet. */}
      {minDocsRequired !== null &&
        (() => {
          const selectedCount = selectedDocuments.length + selectedPastDocuments.length;
          if (selectedCount === 0 || selectedCount >= minDocsRequired) return null;
          const hint = (workflowType && DOC_COUNT_HINT_COPY[workflowType]) || `This workflow needs at least ${minDocsRequired} documents.`;
          return (
            <p className="text-sm text-accent dark:text-dark-muted flex items-start gap-1.5">
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {hint}
            </p>
          );
        })()}

      {/* Consolidated Document Status */}
      {(selectedDocuments.length > 0 || selectedPastDocuments.length > 0 || selectedPastReports.length > 0) && (
        <div className="mt-6">
          {selectedDocuments.length > 0 && (documentsReady ? (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <svg className="h-5 w-5 text-green-400 dark:text-green-300" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L8.23 10.661a.75.75 0 00-1.06 1.06l2.21 2.21a.75.75 0 001.137-.073l3.857-5.397z" clipRule="evenodd" />
                </svg>
                <span className="text-green-800 dark:text-green-200 font-medium">Documents Ready for Analysis</span>
              </div>
              <p className="text-green-700 dark:text-green-300 text-sm mt-1">
                {selectedDocuments.length} document{selectedDocuments.length !== 1 ? 's' : ''} uploaded and processed successfully.
              </p>
              <p className="text-green-600 dark:text-green-400 text-xs mt-2">
                Click "Next" below to proceed with the analysis.
              </p>
            </div>
          ) : (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 dark:border-blue-400"></div>
                <span className="text-blue-800 dark:text-blue-200 font-medium">Processing Documents...</span>
              </div>
              <p className="text-blue-700 dark:text-blue-300 text-sm mt-1">
                {selectedDocuments.filter(doc => documentStatuses[doc.id] === 'processing').length} document{selectedDocuments.filter(doc => documentStatuses[doc.id] === 'processing').length !== 1 ? 's' : ''} being processed.
                {selectedDocuments.filter(doc => documentStatuses[doc.id] === 'completed').length > 0 && (
                  <span className="text-green-600 dark:text-green-400 ml-1">
                    ({selectedDocuments.filter(doc => documentStatuses[doc.id] === 'completed').length} completed)
                  </span>
                )}
              </p>
              <p className="text-blue-600 dark:text-blue-400 text-xs mt-2">
                Please wait while documents are being processed and OCR is completed where needed.
              </p>
            </div>
          ))}

          {/* Consolidated Document List */}
          <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900 dark:text-white">
                All Selected Documents ({selectedDocuments.length + selectedPastDocuments.length + selectedPastReports.length})
              </h4>
              {(selectedDocuments.length > 0 || selectedPastDocuments.length > 0 || selectedPastReports.length > 0) && (
                <button
                  onClick={() => {
                    setSelectedDocuments([]);
                    setSelectedPastDocuments([]);
                    setSelectedPastReports([]);
                    updateParentSelection([], [], []);
                    setDocumentsReady(false);
                  }}
                  className="text-xs text-red-600 hover:text-red-800 underline"
                >
                  Clear All
                </button>
              )}
            </div>
            <div className="space-y-3">
              {selectedDocuments.map((doc, index) => {
                const status = documentStatuses[doc.id] || 'checking';
                const statusConfig = {
                  checking: { color: 'text-yellow-600 dark:text-yellow-300', bg: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700', icon: '🔍', text: 'Checking...' },
                  processing: { color: 'text-blue-600 dark:text-blue-300', bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700', icon: '⏳', text: 'Processing...' },
                  completed: { color: 'text-green-600 dark:text-green-300', bg: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700', icon: '✅', text: 'Ready' },
                  error: { color: 'text-red-600 dark:text-red-300', bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700', icon: '❌', text: 'Error' }
                }[status];

                return (
                  <div key={doc.id} className={`flex items-center justify-between p-3 ${statusConfig.bg} rounded-lg border`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900 dark:text-white truncate">
                          {index + 1}. {doc.name}
                        </span>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusConfig.color} bg-white dark:bg-gray-800`}>
                          <span className="mr-1">{statusConfig.icon}</span>
                          {statusConfig.text}
                        </span>
                      </div>
                      <div className="text-gray-600 dark:text-gray-400 text-xs">
                        {doc.size && `${(doc.size / (1024 * 1024)).toFixed(1)} MB`}
                        {doc.type && ` • ${doc.type.replace('_', ' ')}`}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const updatedDocs = selectedDocuments.filter(d => d.id !== doc.id);
                        setSelectedDocuments(updatedDocs);
                        updateParentSelection(updatedDocs, selectedPastDocuments, selectedPastReports);
                        if (updatedDocs.length === 0) {
                          setDocumentsReady(false);
                        }
                      }}
                      className="ml-3 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 p-1 transition-colors"
                      title="Remove document"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                      </svg>
                    </button>
                  </div>
                );
              })}

              {/* Past Documents */}
              {selectedPastDocuments.map((doc) => (
                <div key={`past-doc-${doc.id}`} className="border border-blue-200 dark:border-blue-700 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-blue-500 flex-shrink-0"></div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                            {doc.originalName}
                          </span>
                          <span className="text-xs px-2 py-1 bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded">
                            Past Document
                          </span>
                          <DocumentIntelligenceChip documentId={doc.id} compact />
                        </div>
                        <div className="text-xs text-blue-700 dark:text-blue-300">
                          {doc.size && `${(doc.size / 1024 / 1024).toFixed(2)} MB`}
                          {doc.uploadedAt && ` • Uploaded: ${new Date(doc.uploadedAt).toLocaleDateString()}`}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const updatedPastDocs = selectedPastDocuments.filter(d => d.id !== doc.id);
                        setSelectedPastDocuments(updatedPastDocs);
                        updateParentSelection(selectedDocuments, updatedPastDocs, selectedPastReports);
                      }}
                      className="ml-3 text-blue-400 hover:text-red-600 p-1 transition-colors"
                      title="Remove past document"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}

              {/* Past Reports */}
              {selectedPastReports.map((report) => (
                <div key={`past-report-${report.id}`} className="border border-green-200 dark:border-green-700 rounded-lg p-4 bg-green-50 dark:bg-green-900/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0"></div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-green-900 dark:text-green-100">
                            {report.title}
                          </span>
                          <span className="text-xs px-2 py-1 bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200 rounded">
                            Past Report
                          </span>
                        </div>
                        <div className="text-xs text-green-700 dark:text-green-300">
                          {report.workflowType && `Type: ${formatWorkflowLabel(report.workflowType)}`}
                          {report.createdAt && ` • Created: ${new Date(report.createdAt).toLocaleDateString()}`}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const updatedPastReports = selectedPastReports.filter(r => r.id !== report.id);
                        setSelectedPastReports(updatedPastReports);
                        updateParentSelection(selectedDocuments, selectedPastDocuments, updatedPastReports);
                      }}
                      className="ml-3 text-green-400 hover:text-red-600 p-1 transition-colors"
                      title="Remove past report"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Document Combination */}
          {(selectedDocuments.length === 0 || documentsReady) &&
            (selectedDocuments.length + selectedPastDocuments.length) >= 2 && (
            <DocumentCombination
              documents={[
                ...selectedDocuments,
                ...selectedPastDocuments.map(d => ({
                  id: d.id,
                  name: d.originalName || d.name,
                  type: d.type,
                  size: d.size,
                  status: 'completed' as const,
                })),
              ]}
              onCombinationReady={(combinedDoc, sourceDocumentIds) => {
                if (combinedDoc && sourceDocumentIds) {
                  const sourceIdSet = new Set(sourceDocumentIds);
                  const remainingUploads = selectedDocuments.filter(doc => !sourceIdSet.has(doc.id));
                  const remainingPastDocs = selectedPastDocuments.filter(doc => !sourceIdSet.has(doc.id));
                  const updatedUploads = [...remainingUploads, combinedDoc];

                  setSelectedDocuments(updatedUploads);
                  setSelectedPastDocuments(remainingPastDocs);

                  // Set the combined document status to completed to avoid "Checking..." spinner
                  setDocumentStatuses(prev => {
                    const updated = { ...prev };
                    sourceDocumentIds.forEach(id => delete updated[id]);
                    updated[combinedDoc.id] = 'completed';
                    return updated;
                  });

                  updateParentSelection(updatedUploads, remainingPastDocs, selectedPastReports);
                }
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

// Additional Context Step Component
interface AdditionalContextStepProps {
  onContextSubmit: (context: string, pastDocuments?: any[], pastReports?: any[], additionalFiles?: UploadedFile[]) => void;
  placeholder?: string;
  customerId?: string;
  projectId?: string;
  initialValue?: string;
}

export const AdditionalContextStep: React.FC<AdditionalContextStepProps> = ({
  onContextSubmit,
  placeholder = "Add any specific instructions or context for the renewal analysis...",
  customerId,
  projectId,
  initialValue
}) => {
  const [context, setContext] = useState(initialValue || '');
  const [selectedPastDocuments, setSelectedPastDocuments] = useState<any[]>([]);
  const [selectedPastReports, setSelectedPastReports] = useState<any[]>([]);
  const [additionalFiles, setAdditionalFiles] = useState<UploadedFile[]>([]);
  
  // Auto-submit context when component mounts and whenever any data changes
  React.useEffect(() => {
    onContextSubmit(context, selectedPastDocuments, selectedPastReports, additionalFiles);
  }, [context, selectedPastDocuments, selectedPastReports, additionalFiles, onContextSubmit]);
  
  const handlePastDocumentsSelected = (documents: any[]) => {
    setSelectedPastDocuments(documents);
  };

  const handlePastReportsSelected = (reports: any[]) => {
    setSelectedPastReports(reports);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
        Add Additional Context
      </h3>

      {/* Past Documents and Reports Selection */}
      {customerId && (
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900 dark:text-white">
            Include Past Documents and Reports
          </h4>
          <PastDocumentSelector
            customerId={customerId}
            projectId={projectId}
            onDocumentsSelected={handlePastDocumentsSelected}
            onReportsSelected={handlePastReportsSelected}
            selectedDocuments={selectedPastDocuments}
            selectedReports={selectedPastReports}
          />
        </div>
      )}
      
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Additional Instructions or Context (Optional)
        </label>
        
        <textarea
          value={context}
          onChange={(e) => setContext(e.target.value)}
          placeholder={placeholder}
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                   focus:outline-none focus:ring-2 focus:ring-blue-500 
                   dark:bg-gray-700 dark:text-white resize-vertical"
        />
        
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Provide any specific instructions, focus areas, or context that should be considered during the analysis.
          You can also select past documents and reports above to include their content in the analysis.
        </div>
      </div>

      {/* Additional Document Upload */}
      {customerId && (
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900 dark:text-white">
            Upload Additional Documents (Optional)
          </h4>
          <FileUploader
            onFilesUploaded={setAdditionalFiles}
            customerId={customerId}
            projectId={projectId}
            acceptedTypes={STANDARD_ACCEPTED_TYPES}
            maxFiles={10}
            maxFileSize={25}
          />
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Report Options Step — opt-in add-on sections for renewal-style workflows
// ============================================================================

export interface ReportOptionsValue {
  schedules: boolean;
  coverageDetails: boolean;
  complianceContractual: boolean;
}

const DEFAULT_REPORT_OPTIONS: ReportOptionsValue = {
  schedules: false,
  coverageDetails: false,
  complianceContractual: false,
};

interface ReportOptionsStepProps {
  value?: ReportOptionsValue;
  onChange: (options: ReportOptionsValue) => void;
}

export const ReportOptionsStep: React.FC<ReportOptionsStepProps> = ({ value, onChange }) => {
  const options = value || DEFAULT_REPORT_OPTIONS;

  // Surface defaults to parent on first mount so workflowData has a value
  React.useEffect(() => {
    if (!value) {
      onChange(DEFAULT_REPORT_OPTIONS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (key: keyof ReportOptionsValue) => {
    onChange({ ...options, [key]: !options[key] });
  };

  const items: Array<{
    key: keyof ReportOptionsValue;
    label: string;
    description: string;
  }> = [
    {
      key: 'schedules',
      label: 'Schedules',
      description:
        'Adds dedicated matrices for every schedule found in the policies — vehicles, drivers, properties/locations, equipment, additional insureds. Each row enumerated with year-over-year additions and removals.',
    },
    {
      key: 'coverageDetails',
      label: 'Coverage Details',
      description:
        'Deductibles & retentions, sub-limits, enhancement endorsement limits, exclusions diff (added / removed / modified), coverage triggers, and key policy definitions — broken out as their own focused sections.',
    },
    {
      key: 'complianceContractual',
      label: 'Compliance & Contractual',
      description:
        'Pulls Certificate of Insurance requirements out of any uploaded contracts, then maps them against the renewal policies to flag compliance gaps and contractual obligations.',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          Optional Report Sections
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          The standard renewal report is generated by default. Select any add-on sections
          you also want included — these expand both the AI's analysis depth and the
          structure of the final report.
        </p>
      </div>

      <div className="space-y-3">
        {items.map((item) => {
          const checked = options[item.key];
          return (
            <label
              key={item.key}
              className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                checked
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(item.key)}
                className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">{item.label}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  {item.description}
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};

// Renewal Report Generation Component
interface RenewalReportGenerationProps {
  oldApplication: any;
  newApplication: any;
  additionalContext: string;
  clientId: string;
  projectId: string;
  onComplete: (reportId: string) => void;
}

// Renewal Analysis Component - Following exact LossRunAnalysis pattern
export const RenewalAnalysis: React.FC<{
  documents: any[];
  additionalContext: string;
  clientId: string;
  projectId?: string;
  pastReports?: any[];
  additionalPastDocuments?: any[];
  onComplete: (result: any) => void;
  onError?: (error: string) => void;
  onExecutionStart?: (workflowInfo: { workflowType: string; documents: any[] }) => void;
  onJobQueued?: (jobId: string) => void;
  token?: string | null;
  loading: boolean;
  reportTitle?: string;
  onReportTitleChange?: (title: string) => void;
}> = ({ documents, additionalContext, clientId, projectId, pastReports, additionalPastDocuments, onComplete, onError, onExecutionStart, onJobQueued, token, loading, reportTitle, onReportTitleChange }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Initializing...');
  const [error, setError] = useState<CategorizedError | null>(null);

  // Listen for progress updates
  useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      const { progress: newProgress, status } = event.detail;
      devLog('📊 Renewal Progress update received:', { progress: newProgress, status });
      setProgress(newProgress);
      setProgressMessage(getProgressMessage(newProgress));
    };

    window.addEventListener('renewalProgress' as any, handleProgressUpdate);

    return () => {
      window.removeEventListener('renewalProgress' as any, handleProgressUpdate);
    };
  }, []);

  const getProgressMessage = (progress: number): string => {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading current documents...';
    if (progress < 30) return 'Loading past reports...';
    if (progress < 40) return 'Preparing renewal analysis...';
    if (progress < 50) return 'Starting renewal analysis...';
    if (progress < 70) return 'AI analyzing renewal options... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  };

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      setProgress(0);
      setProgressMessage('Starting analysis...');
      setError(null);

      // Extract document IDs for job queue
      const documentIds = documents?.map((d: any) => d.id) || [];
      const pastReportIds = pastReports?.map((r: any) => r.id) || [];

      // Try to submit to job queue first (if token available)
      if (token && onJobQueued) {
        try {
          devLog('📤 RenewalAnalysis: Submitting to job queue...');

          const jobResponse = await jobsService.submitJob(token, {
            workflowType: 'renewal_analysis',
            customerId: clientId,
            projectId: projectId,
            documents: documentIds,
            pastReports: pastReportIds,
            additionalContext: additionalContext,
            reportTitle: reportTitle || undefined,
          });

          if (jobResponse.success) {
            devLog(`✅ RenewalAnalysis: Job queued successfully: ${jobResponse.jobId}`);
            setIsAnalyzing(false);
            onJobQueued(jobResponse.jobId);
            return; // Exit early - job is queued
          }
        } catch (queueError) {
          // Job queue not available, fall back to synchronous execution
          devLog('⚠️ RenewalAnalysis: Job queue not available, falling back to synchronous execution:', queueError);
        }
      }

      // FALLBACK: Synchronous execution
      // Notify parent that execution is starting (for background timer)
      if (onExecutionStart) {
        onExecutionStart({ workflowType: 'renewal_analysis', documents });
      }

      devLog('🔄 RenewalAnalysis: Starting analysis with:', {
        documentsCount: documents?.length,
        pastReportsCount: pastReports?.length,
        additionalContext,
        clientId,
        projectId,
        documentIds: documents?.map(d => d.id),
        pastReportIds: pastReports?.map(r => r.id)
      });

      const analysisResult = await workflowService.executeRenewalAnalysis({
        documents,
        additionalContext,
        clientId,
        projectId,
        pastReports,
        reportTitle: reportTitle || undefined
      });

      devLog('✅ RenewalAnalysis: Analysis completed:', {
        hasResult: !!analysisResult,
        resultId: analysisResult?.id,
        hasContent: !!analysisResult?.content
      });

      setResult(analysisResult);
      setProgress(100);
      setProgressMessage('Complete!');
      onComplete(analysisResult);
    } catch (err) {
      console.error('❌ RenewalAnalysis: Error analyzing renewal documents:', err);
      const categorized = categorizeError(err);
      setError(categorized);
      setProgressMessage(categorized.userMessage);
      if (onError) {
        onError(categorized.technicalMessage);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Renewal Analysis</h3>
      
      {isAnalyzing && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                Analyzing Renewal Documents
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                {progressMessage}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                Processing {documents.length} document{documents.length !== 1 ? 's' : ''} with AI analysis...
              </p>
            </div>
          </div>
          {/* Real-time progress bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-blue-700 dark:text-blue-300 mb-2">
              <span>{progress}% complete</span>
              <span>{progress < 100 ? 'Processing...' : 'Done!'}</span>
            </div>
            <div className="bg-blue-200 dark:bg-blue-800 rounded-full h-3 overflow-hidden">
              <div
                className="bg-blue-500 dark:bg-blue-400 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error display with retry button */}
      {error && !isAnalyzing && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-red-800 dark:text-red-200">
                {error.userMessage}
              </h4>
              {error.suggestedAction && (
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {error.suggestedAction}
                </p>
              )}
              <details className="mt-2">
                <summary className="text-xs text-red-600 dark:text-red-400 cursor-pointer hover:underline">
                  Technical details
                </summary>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-mono bg-red-100 dark:bg-red-900/40 p-2 rounded">
                  {error.technicalMessage}
                </p>
              </details>
              {error.isRetryable && (
                <button
                  onClick={() => {
                    setError(null);
                    handleAnalyze();
                  }}
                  className="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                >
                  Retry Analysis
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!result && !isAnalyzing && !error && (
        <div className="space-y-4">
          {/* Report Title Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Report Title (Optional)
            </label>
            <input
              type="text"
              value={reportTitle || ''}
              onChange={(e) => onReportTitleChange?.(e.target.value)}
              placeholder="Leave blank to auto-generate title"
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-blue-500
                       dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Customize the report title or leave blank to use the default format
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
            >
              Execute Renewal Analysis
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
            Renewal Analysis Complete
          </h4>
          <p className="text-sm text-green-700 dark:text-green-300">
            {result.summary || 'Renewal analysis completed successfully'}
          </p>
        </div>
      )}
    </div>
  );
};

export const YearOverYearAnalysis: React.FC<{
  documents: any[];
  additionalContext: string;
  clientId: string;
  projectId?: string;
  pastReports?: any[];
  additionalPastDocuments?: any[];
  onComplete: (result: any) => void;
  onError?: (error: string) => void;
  onExecutionStart?: (workflowInfo: { workflowType: string; documents: any[] }) => void;
  onJobQueued?: (jobId: string) => void;
  token?: string | null;
  loading: boolean;
  workflowType?: 'year_over_year' | 'yoy_renewal' | 'personal_lines_yoy_renewal' | 'commercial_lines_exec_renewal';
  reportTitle?: string;
  onReportTitleChange?: (title: string) => void;
  reportOptions?: ReportOptionsValue;
}> = ({ documents, additionalContext, clientId, projectId, pastReports, additionalPastDocuments, onComplete, onError, onExecutionStart, onJobQueued, token, loading, workflowType = 'year_over_year', reportTitle, onReportTitleChange, reportOptions }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Initializing...');
  const [error, setError] = useState<CategorizedError | null>(null);

  // Determine event name based on workflow type
  const isUnifiedWorkflow = workflowType === 'yoy_renewal' || workflowType === 'personal_lines_yoy_renewal' || workflowType === 'commercial_lines_exec_renewal';
  const progressEventName = isUnifiedWorkflow ? 'yoyRenewalProgress' : 'yearOverYearProgress';
  const isYoyRenewal = workflowType === 'yoy_renewal' || workflowType === 'personal_lines_yoy_renewal' || workflowType === 'commercial_lines_exec_renewal';

  // Listen for progress updates
  useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      const { progress: newProgress, status } = event.detail;
      devLog(`📊 ${isYoyRenewal ? 'YOY Renewal' : 'Year Over Year'} Progress update received:`, { progress: newProgress, status });
      setProgress(newProgress);
      setProgressMessage(getProgressMessage(newProgress));
    };

    window.addEventListener(progressEventName as any, handleProgressUpdate);

    return () => {
      window.removeEventListener(progressEventName as any, handleProgressUpdate);
    };
  }, [progressEventName, isYoyRenewal]);

  const getProgressMessage = (progress: number): string => {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading current documents...';
    if (progress < 30) return 'Loading past reports...';
    if (progress < 40) return 'Preparing comparison data...';
    if (progress < 50) return 'Starting year-over-year analysis...';
    if (progress < 70) return 'AI analyzing policy changes... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  };

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      setProgress(0);
      setProgressMessage('Starting analysis...');
      setError(null);

      // Extract document IDs
      const documentIds = documents?.map((d: any) => d.id) || [];
      const pastReportIds = pastReports?.map((r: any) => r.id) || [];

      // Try to submit to job queue first (if token available)
      if (token && onJobQueued) {
        try {
          devLog(`📤 ${isYoyRenewal ? 'YOYRenewalAnalysis' : 'YearOverYearAnalysis'}: Submitting to job queue...`);

          const jobResponse = await jobsService.submitJob(token, {
            workflowType: workflowType || 'year_over_year',
            customerId: clientId,
            projectId: projectId,
            documents: documentIds,
            pastReports: pastReportIds,
            additionalContext: additionalContext,
            reportTitle: reportTitle || undefined,
            reportOptions: reportOptions as unknown as Record<string, boolean> | undefined,
          });

          if (jobResponse.success) {
            devLog(`✅ ${isYoyRenewal ? 'YOYRenewalAnalysis' : 'YearOverYearAnalysis'}: Job queued successfully: ${jobResponse.jobId}`);
            setIsAnalyzing(false);
            onJobQueued(jobResponse.jobId);
            return; // Exit early - job is queued
          }
        } catch (queueError) {
          devLog(`⚠️ ${isYoyRenewal ? 'YOYRenewalAnalysis' : 'YearOverYearAnalysis'}: Job queue not available, falling back to synchronous execution:`, queueError);
        }
      }

      // FALLBACK: Synchronous execution
      // Notify parent that execution is starting (for background timer)
      if (onExecutionStart) {
        onExecutionStart({ workflowType: workflowType || 'year_over_year', documents });
      }

      devLog(`🔄 ${isYoyRenewal ? 'YOYRenewalAnalysis' : 'YearOverYearAnalysis'}: Starting synchronous analysis with:`, {
        documentsCount: documents?.length,
        pastReportsCount: pastReports?.length,
        additionalContext,
        clientId,
        projectId,
        documentIds: documents?.map(d => d.id),
        pastReportIds: pastReports?.map(r => r.id),
        workflowType
      });

      // Use appropriate service based on workflow type
      const analysisResult = isYoyRenewal
        ? await workflowService.executeYoyRenewalAnalysis({
            documents,
            additionalContext,
            clientId,
            projectId,
            pastReports,
            workflowType: workflowType || 'yoy_renewal', // Pass the actual workflow type for the correct endpoint
            reportTitle: reportTitle || undefined,
            reportOptions: reportOptions as unknown as Record<string, boolean> | undefined,
          })
        : await workflowService.executeYearOverYearAnalysisNew({
            documents,
            additionalContext,
            clientId,
            projectId,
            pastReports,
            reportTitle: reportTitle || undefined
          });

      devLog(`✅ ${isYoyRenewal ? 'YOYRenewalAnalysis' : 'YearOverYearAnalysis'}: Analysis completed:`, {
        hasResult: !!analysisResult,
        resultId: analysisResult?.id,
        hasContent: !!analysisResult?.content
      });

      setResult(analysisResult);
      setProgress(100);
      setProgressMessage('Complete!');
      onComplete(analysisResult);
    } catch (err) {
      console.error(`❌ ${isYoyRenewal ? 'YOYRenewalAnalysis' : 'YearOverYearAnalysis'}: Error analyzing documents:`, err);
      const categorized = categorizeError(err);
      setError(categorized);
      setProgressMessage(categorized.userMessage);
      if (onError) {
        onError(categorized.technicalMessage);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">{
        workflowType === 'commercial_lines_exec_renewal' ? 'Executive Renewal Summary' :
        workflowType === 'personal_lines_yoy_renewal' ? 'Personal Lines Renewal Review' :
        isYoyRenewal ? 'Multi-Line Renewal Review' : 'Year-over-Year Comparison'
      }</h3>
      
      {isAnalyzing && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                {workflowType === 'commercial_lines_exec_renewal' ? 'Performing Executive Renewal Analysis' :
                 workflowType === 'personal_lines_yoy_renewal' ? 'Performing Personal Lines YOY Analysis' :
                 'Performing Year-Over-Year Analysis'}
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                {progressMessage}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                Comparing {documents.length} document{documents.length !== 1 ? 's' : ''} for policy changes...
              </p>
            </div>
          </div>
          {/* Real-time progress bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-blue-700 dark:text-blue-300 mb-2">
              <span>{progress}% complete</span>
              <span>{progress < 100 ? 'Processing...' : 'Done!'}</span>
            </div>
            <div className="bg-blue-200 dark:bg-blue-800 rounded-full h-3 overflow-hidden">
              <div
                className="bg-blue-500 dark:bg-blue-400 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error display with retry button */}
      {error && !isAnalyzing && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-red-800 dark:text-red-200">
                {error.userMessage}
              </h4>
              {error.suggestedAction && (
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {error.suggestedAction}
                </p>
              )}
              <details className="mt-2">
                <summary className="text-xs text-red-600 dark:text-red-400 cursor-pointer hover:underline">
                  Technical details
                </summary>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-mono bg-red-100 dark:bg-red-900/40 p-2 rounded">
                  {error.technicalMessage}
                </p>
              </details>
              {error.isRetryable && (
                <button
                  onClick={() => {
                    setError(null);
                    handleAnalyze();
                  }}
                  className="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                >
                  Retry Analysis
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!result && !isAnalyzing && !error && (
        <div className="space-y-4">
          {/* Report Title Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Report Title (Optional)
            </label>
            <input
              type="text"
              value={reportTitle || ''}
              onChange={(e) => onReportTitleChange?.(e.target.value)}
              placeholder="Leave blank to auto-generate title"
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-blue-500
                       dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Customize the report title or leave blank to use the default format
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
            >
              {workflowType === 'commercial_lines_exec_renewal' ? 'Execute Executive Renewal Analysis' :
               workflowType === 'personal_lines_yoy_renewal' ? 'Execute Personal Lines YOY Analysis' :
               'Execute Year-Over-Year Analysis'}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
            Year-Over-Year Analysis Complete
          </h4>
          <p className="text-sm text-green-700 dark:text-green-300">
            {result.summary || 'Year-over-year analysis completed successfully'}
          </p>
        </div>
      )}
    </div>
  );
};

export const InsuranceRequirementsAnalysis: React.FC<{
  documents: any[];
  additionalContext: string;
  clientId: string;
  projectId?: string;
  onComplete: (result: any) => void;
  onError?: (error: string) => void;
  onExecutionStart?: (workflowInfo: { workflowType: string; documents: any[] }) => void;
  onJobQueued?: (jobId: string) => void;
  token?: string | null;
  loading: boolean;
  reportTitle?: string;
  onReportTitleChange?: (title: string) => void;
}> = ({ documents, additionalContext, clientId, projectId, onComplete, onError, onExecutionStart, onJobQueued, token, loading, reportTitle, onReportTitleChange }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Initializing...');
  const [error, setError] = useState<CategorizedError | null>(null);

  // Listen for progress updates
  useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      const { progress: newProgress, status } = event.detail;
      devLog('📊 Insurance Requirements Progress update received:', { progress: newProgress, status });
      setProgress(newProgress);
      setProgressMessage(getProgressMessage(newProgress));
    };

    window.addEventListener('insuranceRequirementsProgress' as any, handleProgressUpdate);

    return () => {
      window.removeEventListener('insuranceRequirementsProgress' as any, handleProgressUpdate);
    };
  }, []);

  const getProgressMessage = (progress: number): string => {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading documents...';
    if (progress < 30) return 'Preparing documents...';
    if (progress < 40) return 'Generating prompts...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 70) return 'AI analyzing insurance requirements... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  };

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      setProgress(0);
      setProgressMessage('Starting analysis...');
      setError(null); // Clear previous errors

      // Extract document IDs
      const documentIds = documents?.map((d: any) => d.id) || [];

      // Try to submit to job queue first (if token available)
      if (token && onJobQueued) {
        try {
          devLog('📤 InsuranceRequirementsAnalysis: Submitting to job queue...');

          const jobResponse = await jobsService.submitJob(token, {
            workflowType: 'insurance_requirements',
            customerId: clientId,
            projectId: projectId,
            documents: documentIds,
            additionalContext: additionalContext,
            reportTitle: reportTitle || undefined,
          });

          if (jobResponse.success) {
            devLog(`✅ InsuranceRequirementsAnalysis: Job queued successfully: ${jobResponse.jobId}`);
            setIsAnalyzing(false);
            onJobQueued(jobResponse.jobId);
            return; // Exit early - job is queued
          }
        } catch (queueError) {
          devLog('⚠️ InsuranceRequirementsAnalysis: Job queue not available, falling back to synchronous execution:', queueError);
        }
      }

      // FALLBACK: Synchronous execution
      // Notify parent that execution is starting (for background timer)
      if (onExecutionStart) {
        onExecutionStart({ workflowType: 'insurance_requirements', documents });
      }

      devLog('🔄 InsuranceRequirementsAnalysis: Starting synchronous analysis with:', {
        documentsCount: documents?.length,
        additionalContext,
        clientId,
        projectId,
        documentIds: documents?.map(d => d.id)
      });

      const analysisResult = await workflowService.executeInsuranceRequirementsAnalysis({
        documents,
        additionalContext,
        clientId,
        projectId,
        reportTitle: reportTitle || undefined
      });

      devLog('✅ InsuranceRequirementsAnalysis: Analysis completed:', {
        hasResult: !!analysisResult,
        resultId: analysisResult?.id,
        hasContent: !!analysisResult?.content
      });

      setResult(analysisResult);
      setProgress(100);
      setProgressMessage('Complete!');
      onComplete(analysisResult);
    } catch (err) {
      console.error('❌ InsuranceRequirementsAnalysis: Error analyzing insurance requirements:', err);
      const categorized = categorizeError(err);
      setError(categorized);
      setProgressMessage(categorized.userMessage);
      // Propagate error to parent if handler provided
      if (onError) {
        onError(categorized.technicalMessage);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Insurance Requirements Analysis</h3>

      {isAnalyzing && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                Analyzing Insurance Requirements
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                {progressMessage}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                Processing {documents.length} document{documents.length !== 1 ? 's' : ''} for compliance analysis...
              </p>
            </div>
          </div>

          {/* Real-time progress bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-blue-700 dark:text-blue-300 mb-2">
              <span>{progress}% complete</span>
              <span>{progress < 100 ? 'Processing...' : 'Done!'}</span>
            </div>
            <div className="bg-blue-200 dark:bg-blue-800 rounded-full h-3 overflow-hidden">
              <div
                className="bg-blue-500 dark:bg-blue-400 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error display with retry button */}
      {error && !isAnalyzing && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-red-800 dark:text-red-200">
                {error.userMessage}
              </h4>
              {error.suggestedAction && (
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {error.suggestedAction}
                </p>
              )}
              <details className="mt-2">
                <summary className="text-xs text-red-600 dark:text-red-400 cursor-pointer hover:underline">
                  Technical details
                </summary>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-mono bg-red-100 dark:bg-red-900/40 p-2 rounded">
                  {error.technicalMessage}
                </p>
              </details>
              {error.isRetryable && (
                <button
                  onClick={() => {
                    setError(null);
                    handleAnalyze();
                  }}
                  className="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                >
                  Retry Analysis
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!result && !isAnalyzing && !error && (
        <div className="space-y-4">
          {/* Report Title Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Report Title (Optional)
            </label>
            <input
              type="text"
              value={reportTitle || ''}
              onChange={(e) => onReportTitleChange?.(e.target.value)}
              placeholder="Leave blank to auto-generate title"
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-blue-500
                       dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Customize the report title or leave blank to use the default format
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
            >
              Execute Insurance Requirements Analysis
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
            Insurance Requirements Analysis Complete
          </h4>
          <p className="text-sm text-green-700 dark:text-green-300">
            {result.summary || 'Insurance requirements analysis completed successfully'}
          </p>
        </div>
      )}
    </div>
  );
};


export const LossRunAnalysis: React.FC<{
  documents: any[];
  additionalContext?: string;
  clientId: string;
  projectId?: string;
  pastReports?: any[];
  additionalPastDocuments?: any[];
  onComplete: (result: any) => void;
  onError?: (error: string) => void;
  onExecutionStart?: (workflowInfo: { workflowType: string; documents: any[] }) => void;
  onJobQueued?: (jobId: string) => void;
  token?: string | null;
  loading: boolean;
  reportTitle?: string;
  onReportTitleChange?: (title: string) => void;
}> = ({ documents, additionalContext, clientId, projectId, pastReports, additionalPastDocuments, onComplete, onError, onExecutionStart, onJobQueued, token, loading, reportTitle, onReportTitleChange }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Initializing...');
  const [error, setError] = useState<CategorizedError | null>(null);

  // Listen for progress updates
  useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      const { progress: newProgress, status } = event.detail;
      devLog('📊 Progress update received:', { progress: newProgress, status });
      setProgress(newProgress);
      setProgressMessage(getProgressMessage(newProgress));
    };

    window.addEventListener('lossRunProgress' as any, handleProgressUpdate);

    return () => {
      window.removeEventListener('lossRunProgress' as any, handleProgressUpdate);
    };
  }, []);

  const getProgressMessage = (progress: number): string => {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading documents...';
    if (progress < 30) return 'Loading past reports...';
    if (progress < 40) return 'Preparing analysis...';
    if (progress < 50) return 'Generating prompts...';
    if (progress < 70) return 'AI analyzing loss runs... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  };

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      setProgress(0);
      setProgressMessage('Starting analysis...');
      setError(null);

      // Notify parent that execution is starting (for background timer)
      if (onExecutionStart) {
        onExecutionStart({ workflowType: 'loss_run_analysis', documents });
      }

      // Extract document IDs for job queue
      const documentIds = documents?.map((d: any) => d.id) || [];
      const pastReportIds = pastReports?.map((r: any) => r.id) || [];

      devLog('🔄 LossRunAnalysis: Starting analysis with:', {
        documentsCount: documents?.length,
        pastReportsCount: pastReports?.length,
        clientId,
        projectId,
        documentIds,
        pastReportIds
      });

      // Try to submit to job queue first (if token available)
      if (token && onJobQueued) {
        try {
          devLog('📤 LossRunAnalysis: Submitting to job queue...');

          const jobResponse = await jobsService.submitJob(token, {
            workflowType: 'loss_run_analysis',
            customerId: clientId,
            projectId: projectId,
            documents: documentIds,
            pastReports: pastReportIds,
            additionalContext: additionalContext,
            reportTitle: reportTitle || undefined,
          });

          if (jobResponse.success) {
            devLog(`✅ LossRunAnalysis: Job queued successfully: ${jobResponse.jobId}`);
            setIsAnalyzing(false);
            onJobQueued(jobResponse.jobId);
            return; // Exit early - job is queued
          }
        } catch (queueError) {
          // Job queue not available, fall back to synchronous execution
          devLog('⚠️ LossRunAnalysis: Job queue not available, falling back to synchronous execution:', queueError);
        }
      }

      // FALLBACK: Synchronous execution
      const analysisResult = await workflowService.executeLossRunAnalysis({
        documents,
        clientId,
        projectId,
        pastReports,
        reportTitle: reportTitle || undefined
      });

      devLog('✅ LossRunAnalysis: Analysis completed:', {
        hasResult: !!analysisResult,
        resultId: analysisResult?.id,
        hasContent: !!analysisResult?.content
      });

      setResult(analysisResult);
      setProgress(100);
      setProgressMessage('Complete!');
      onComplete(analysisResult);
    } catch (err) {
      console.error('❌ LossRunAnalysis: Error analyzing loss runs:', err);
      const categorized = categorizeError(err);
      setError(categorized);
      setProgressMessage(categorized.userMessage);
      if (onError) {
        onError(categorized.technicalMessage);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Loss Run Analysis</h3>

      {isAnalyzing && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                Analyzing Loss Run Data
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                {progressMessage}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                Processing {documents.length} loss run document{documents.length !== 1 ? 's' : ''} for claims patterns...
              </p>
            </div>
          </div>

          {/* Real-time progress bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-blue-700 dark:text-blue-300 mb-2">
              <span>{progress}% complete</span>
              <span>{progress < 100 ? 'Processing...' : 'Done!'}</span>
            </div>
            <div className="bg-blue-200 dark:bg-blue-800 rounded-full h-3 overflow-hidden">
              <div
                className="bg-blue-500 dark:bg-blue-400 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error display with retry button */}
      {error && !isAnalyzing && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-red-800 dark:text-red-200">
                {error.userMessage}
              </h4>
              {error.suggestedAction && (
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {error.suggestedAction}
                </p>
              )}
              <details className="mt-2">
                <summary className="text-xs text-red-600 dark:text-red-400 cursor-pointer hover:underline">
                  Technical details
                </summary>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-mono bg-red-100 dark:bg-red-900/40 p-2 rounded">
                  {error.technicalMessage}
                </p>
              </details>
              {error.isRetryable && (
                <button
                  onClick={() => {
                    setError(null);
                    handleAnalyze();
                  }}
                  className="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                >
                  Retry Analysis
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!result && !isAnalyzing && !error && (
        <div className="space-y-4">
          {/* Report Title Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Report Title (Optional)
            </label>
            <input
              type="text"
              value={reportTitle || ''}
              onChange={(e) => onReportTitleChange?.(e.target.value)}
              placeholder="Leave blank to auto-generate title"
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-blue-500
                       dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Customize the report title or leave blank to use the default format
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
            >
              Execute Loss Run Analysis
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
            Loss Run Analysis Complete
          </h4>
          <p className="text-sm text-green-700 dark:text-green-300">
            {result.summary || 'Loss run analysis completed successfully'}
          </p>
        </div>
      )}
    </div>
  );
};

// ===== RESEARCH TOOL COMPONENTS =====

interface ResearchUrlInputProps {
  onUrlSet: (url: string, additionalContext?: string, documents?: any[], linkedinUrl?: string, companyName?: string) => void;
  clientId: string;
  projectId?: string;
  /** Prefills the Company Name field — the customer's company/display name. */
  clientName?: string;
}

export const ResearchUrlInput: React.FC<ResearchUrlInputProps> = ({ onUrlSet, clientId, projectId, clientName }) => {
  const [url, setUrl] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [companyName, setCompanyName] = useState(clientName || '');
  const [error, setError] = useState('');
  const [urlValid, setUrlValid] = useState(false);
  const [linkedinUrlValid, setLinkedinUrlValid] = useState(false);

  // Normalize URL to handle various input formats
  const normalizeUrl = (input: string): string => {
    let url = input.trim();
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://')) {
      return url;
    }
    if (lowerUrl.startsWith('www.')) {
      url = url.substring(4);
    }
    return `https://www.${url}`;
  };

  // Prefill from the customer's name — emit once so canProceed is satisfied
  // immediately without requiring the user to touch anything.
  useEffect(() => {
    if (clientName?.trim()) {
      onUrlSet('', '', [], '', clientName.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUrlChange = (value: string) => {
    setUrl(value);
    setError('');

    if (value.trim()) {
      try {
        const normalizedUrl = normalizeUrl(value);
        const urlObj = new URL(normalizedUrl);
        if (urlObj.hostname && urlObj.hostname.includes('.')) {
          setUrlValid(true);
          onUrlSet(urlObj.toString(), '', [], linkedinUrlValid ? (linkedinUrl.startsWith('http') ? linkedinUrl : `https://${linkedinUrl}`) : '', companyName);
        } else {
          throw new Error('Invalid domain');
        }
      } catch (err) {
        setUrlValid(false);
        if (value.length > 3) {
          setError('Please enter a valid website URL (e.g., example.com)');
        }
      }
    } else {
      setUrlValid(false);
      onUrlSet('', '', [], linkedinUrlValid ? linkedinUrl : '', companyName);
    }
  };

  const handleLinkedinUrlChange = (value: string) => {
    setLinkedinUrl(value);

    if (value.trim()) {
      try {
        const linkedinPattern = /^(https?:\/\/)?(www\.)?linkedin\.com\/(company|in)\/[^\/\s]+/i;
        if (linkedinPattern.test(value.trim())) {
          const normalizedLinkedinUrl = value.startsWith('http') ? value : `https://${value}`;
          const urlObj = new URL(normalizedLinkedinUrl);
          setLinkedinUrlValid(true);
          const hasValidUrl = urlValid && url.trim();
          const mainUrl = hasValidUrl ? normalizeUrl(url) : '';
          onUrlSet(mainUrl, '', [], urlObj.toString(), companyName);
        } else {
          setLinkedinUrlValid(false);
        }
      } catch (err) {
        setLinkedinUrlValid(false);
      }
    } else {
      setLinkedinUrlValid(false);
      onUrlSet(urlValid && url.trim() ? normalizeUrl(url) : '', '', [], '', companyName);
    }
  };

  const handleCompanyNameChange = (value: string) => {
    setCompanyName(value);
    const mainUrl = urlValid && url.trim() ? normalizeUrl(url) : '';
    const linkedin = linkedinUrlValid ? (linkedinUrl.startsWith('http') ? linkedinUrl : `https://${linkedinUrl}`) : '';
    onUrlSet(mainUrl, '', [], linkedin, value);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Company Research Setup
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Enter the company's name and/or website. Context, past documents, and past reports are added on the next step.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Company Name
          </label>
          <input
            type="text"
            value={companyName}
            onChange={(e) => handleCompanyNameChange(e.target.value)}
            placeholder="Acme Manufacturing Inc."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Company Website URL (Optional)
          </label>
          <input
            type="text"
            value={url}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder="example.com or www.example.com or https://example.com"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            You can enter the URL in any format - we'll automatically add https:// and www if needed
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            LinkedIn Company URL (Optional)
          </label>
          <input
            type="text"
            value={linkedinUrl}
            onChange={(e) => handleLinkedinUrlChange(e.target.value)}
            placeholder="linkedin.com/company/company-name or linkedin.com/in/person-name"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Add a LinkedIn company or person page for additional professional insights
          </p>
          {linkedinUrlValid && (
            <p className="mt-1 text-xs text-green-600 dark:text-green-400">
              ✓ Valid LinkedIn URL
            </p>
          )}
        </div>

        {error && (
          <div className="text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        {(companyName.trim() || urlValid || linkedinUrlValid) && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L8.23 10.661a.75.75 0 00-1.06 1.06l2.21 2.21a.75.75 0 001.137-.073l3.857-5.397z" clipRule="evenodd" />
              </svg>
              <span className="text-green-800 font-medium">Research Target Ready</span>
            </div>
            <p className="text-green-700 text-sm mt-1">
              Risk Ninja will search the web{urlValid ? ' and read the company website' : ''}{linkedinUrlValid ? ' and LinkedIn' : ''} for underwriting-relevant findings.
            </p>
            <p className="text-green-600 text-xs mt-2">
              Click "Next" below to add additional context and documents.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

interface ResearchExecutionProps {
  companyUrl: string;
  linkedinUrl?: string;
  companyName?: string;
  additionalContext?: string;
  documents?: any[];
  additionalPastDocuments?: any[];
  pastReports?: any[];
  onComplete: (result: any) => void;
  onError?: (error: string) => void;
  onExecutionStart?: (workflowInfo: { workflowType: string; documents: any[] }) => void;
  onJobQueued?: (jobId: string) => void;
  token?: string | null;
  loading: boolean;
  clientId: string;
  projectId?: string;
  reportTitle?: string;
  onReportTitleChange?: (title: string) => void;
}

export const ResearchExecution: React.FC<ResearchExecutionProps> = ({
  companyUrl,
  linkedinUrl,
  companyName,
  additionalContext,
  documents,
  additionalPastDocuments,
  pastReports,
  onComplete,
  onError,
  onExecutionStart,
  onJobQueued,
  token,
  loading,
  clientId,
  projectId,
  reportTitle,
  onReportTitleChange
}) => {
  const [isResearching, setIsResearching] = useState(false);
  const [progress, setProgress] = useState('');

  const handleExecute = async () => {
    setIsResearching(true);
    setProgress('Initializing research...');

    // Extract IDs for job queue. additionalPastDocuments includes both past customer
    // documents and newly uploaded files (merged in WorkflowModal.handleContextSubmit).
    const documentIds = documents?.map((d: any) => d.id).filter(Boolean) || [];
    const additionalDocIds = additionalPastDocuments?.map((d: any) => d.id).filter(Boolean) || [];
    const pastReportIds = pastReports?.map((r: any) => r.id).filter(Boolean) || [];

    if (!token || !onJobQueued) {
      const msg = 'Queue service unavailable. Please refresh and try again.';
      setProgress(`Error: ${msg}`);
      setIsResearching(false);
      if (onError) onError(msg);
      return;
    }

    try {
      devLog('📤 ResearchExecution: Submitting to job queue...', {
        documentCount: documentIds.length,
        additionalDocCount: additionalDocIds.length,
        pastReportCount: pastReportIds.length,
      });

      const jobResponse = await jobsService.submitJob(token, {
        workflowType: 'research',
        customerId: clientId,
        projectId: projectId,
        documents: documentIds,
        additionalDocuments: additionalDocIds,
        pastReports: pastReportIds,
        additionalContext: additionalContext,
        context: JSON.stringify({ companyUrl, linkedinUrl, companyName }), // Pass name/URLs as context
        reportTitle: reportTitle || undefined,
      });

      if (jobResponse.success) {
        devLog(`✅ ResearchExecution: Job queued successfully: ${jobResponse.jobId}`);
        setIsResearching(false);
        onJobQueued(jobResponse.jobId);
        return;
      }

      throw new Error('Job queue rejected the submission');
    } catch (queueError) {
      const msg = queueError instanceof Error ? queueError.message : 'Failed to queue research job';
      console.error('❌ ResearchExecution: Queue submission failed:', queueError);
      setProgress(`Error: ${msg}`);
      setIsResearching(false);
      if (onError) onError(msg);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Execute Research Analysis
        </h3>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Research Target</h4>
        <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
          <strong>Company URL:</strong> {companyUrl}
        </p>
        {linkedinUrl && (
          <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
            <strong>LinkedIn URL:</strong> {linkedinUrl}
          </p>
        )}
        {additionalContext && (
          <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
            <strong>Focus:</strong> {additionalContext}
          </p>
        )}
        {documents && documents.length > 0 && (
          <div className="mt-3 pt-3 border-t border-blue-300 dark:border-blue-700">
            <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-1">
              Supporting Documents ({documents.length}):
            </p>
            <div className="space-y-1">
              {documents.map((doc, index) => (
                <p key={doc.id} className="text-xs text-blue-700 dark:text-blue-300">
                  • {doc.name}
                </p>
              ))}
            </div>
          </div>
        )}
        {((additionalPastDocuments && additionalPastDocuments.length > 0) || (pastReports && pastReports.length > 0)) && (
          <div className="mt-3 pt-3 border-t border-blue-300 dark:border-blue-700 space-y-1">
            {additionalPastDocuments && additionalPastDocuments.length > 0 && (
              <p className="text-sm text-blue-700 dark:text-blue-300">
                + {additionalPastDocuments.length} past document{additionalPastDocuments.length !== 1 ? 's' : ''} for context
              </p>
            )}
            {pastReports && pastReports.length > 0 && (
              <p className="text-sm text-blue-700 dark:text-blue-300">
                + {pastReports.length} past report{pastReports.length !== 1 ? 's' : ''} for context
              </p>
            )}
          </div>
        )}
      </div>

      {isResearching && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">
            Research In Progress...
          </h4>
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            {progress}
          </p>
          <div className="mt-3 bg-yellow-200 dark:bg-yellow-800 rounded-full h-2">
            <div className="bg-yellow-500 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
          </div>
        </div>
      )}

      {!isResearching && (
        <div className="space-y-4">
          {/* Report Title Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Report Title (Optional)
            </label>
            <input
              type="text"
              value={reportTitle || ''}
              onChange={(e) => onReportTitleChange?.(e.target.value)}
              placeholder="Leave blank to auto-generate title"
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-blue-500
                       dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Customize the report title or leave blank to use the default format
            </p>
          </div>

          <button
            onClick={handleExecute}
            disabled={loading}
            className="w-full px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Starting Research...' : 'Start Research Analysis'}
          </button>
        </div>
      )}
    </div>
  );
};


/**
 * Loss Run Analyzer Component
 * Forensic multi-document loss run analysis with claim development tracking
 */
export const LossRunAnalyzer: React.FC<{
  documents: any[];
  additionalContext?: string;
  clientId: string;
  projectId?: string;
  pastReports?: any[];
  onComplete: (result: any) => void;
  onError?: (error: string) => void;
  onExecutionStart?: (workflowInfo: { workflowType: string; documents: any[] }) => void;
  onJobQueued?: (jobId: string) => void;
  token?: string | null;
  loading: boolean;
  reportTitle?: string;
  onReportTitleChange?: (title: string) => void;
  workflowType?: 'loss_run_analyzer' | 'loss_run_analyzer_v4' | 'loss_run_analyzer_v4_3';
}> = ({ documents, additionalContext, clientId, projectId, pastReports, onComplete, onError, onExecutionStart, onJobQueued, token, loading, reportTitle, onReportTitleChange, workflowType = 'loss_run_analyzer' }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Initializing...');
  const [error, setError] = useState<CategorizedError | null>(null);
  const [analysisDepth, setAnalysisDepth] = useState<'quick' | 'deep' | null>(null);

  // Listen for progress updates
  useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      const { progress: newProgress, status } = event.detail;
      devLog('📊 Loss Run Analyzer progress update:', { progress: newProgress, status });
      setProgress(newProgress);
      setProgressMessage(getProgressMessage(newProgress));
    };

    window.addEventListener('lossRunAnalyzerProgress' as any, handleProgressUpdate);

    return () => {
      window.removeEventListener('lossRunAnalyzerProgress' as any, handleProgressUpdate);
    };
  }, []);

  const getProgressMessage = (progress: number): string => {
    if (progress < 10) return 'Initializing forensic analysis...';
    if (progress < 20) return 'Loading documents...';
    if (progress < 30) return 'Detecting loss run formats...';
    if (progress < 40) return 'Building claim census...';
    if (progress < 50) return 'Preparing extraction prompts...';
    if (progress < 70) return 'AI analyzing loss runs... (this may take 3-7 minutes)';
    if (progress < 80) return 'Cross-document reconciliation...';
    if (progress < 90) return 'Generating pattern analysis...';
    if (progress < 100) return 'Saving report...';
    return 'Complete!';
  };

  const handleAnalyze = async (depth: 'quick' | 'deep') => {
    try {
      setAnalysisDepth(depth);
      setIsAnalyzing(true);
      setProgress(0);
      setProgressMessage(depth === 'quick' ? 'Starting quick analysis...' : 'Starting deep forensic analysis...');
      setError(null);

      // Extract document IDs
      const documentIds = documents?.map((d: any) => d.id) || [];
      const pastReportIds = pastReports?.map((r: any) => r.id) || [];

      // Try to submit to job queue first (if token available)
      if (token && onJobQueued) {
        try {
          devLog(`📤 LossRunAnalyzer: Submitting ${depth} analysis to job queue...`);

          const jobResponse = await jobsService.submitJob(token, {
            workflowType,
            customerId: clientId,
            projectId: projectId,
            documents: documentIds,
            pastReports: pastReportIds,
            additionalContext: additionalContext,
            reportTitle: reportTitle || undefined,
            analysisMode: depth,
          });

          if (jobResponse.success) {
            devLog(`✅ LossRunAnalyzer: Job queued successfully: ${jobResponse.jobId}`);
            setIsAnalyzing(false);
            onJobQueued(jobResponse.jobId);
            return; // Exit early - job is queued
          }
        } catch (queueError) {
          // Job queue not available, fall back to synchronous execution
          devLog('⚠️ LossRunAnalyzer: Job queue not available, falling back to synchronous execution:', queueError);
        }
      }

      // FALLBACK: Synchronous execution (v3.1 path only — v4.x must use job queue)
      if (workflowType === 'loss_run_analyzer_v4') {
        throw new Error('Loss Run Analyzer (legacy NEW) requires the job queue. Sync fallback is not supported for v4.');
      }
      if (workflowType === 'loss_run_analyzer_v4_3') {
        throw new Error('Loss Run Analysis requires the job queue. Sync fallback is not supported for v4.3.');
      }

      // Notify parent that execution is starting (for background timer)
      if (onExecutionStart) {
        onExecutionStart({ workflowType, documents });
      }

      devLog('🔄 LossRunAnalyzer: Starting synchronous analysis with:', {
        documentsCount: documents?.length,
        pastReportsCount: pastReports?.length,
        clientId,
        projectId,
        documentIds: documents?.map(d => d.id),
        pastReportIds: pastReports?.map(r => r.id)
      });

      const analysisResult = await workflowService.executeLossRunAnalyzer({
        documents,
        clientId,
        projectId,
        context: additionalContext,
        pastReports,
        reportTitle: reportTitle || undefined
      });

      devLog('✅ LossRunAnalyzer: Analysis completed:', {
        hasResult: !!analysisResult,
        resultId: analysisResult?.id,
        hasContent: !!analysisResult?.content
      });

      setResult(analysisResult);
      setProgress(100);
      setProgressMessage('Complete!');
      onComplete(analysisResult);
    } catch (err) {
      console.error('❌ LossRunAnalyzer: Error analyzing loss runs:', err);
      const categorized = categorizeError(err);
      setError(categorized);
      setProgressMessage(categorized.userMessage);
      if (onError) {
        onError(categorized.technicalMessage);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Loss Run Analyzer</h3>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Forensic multi-document analysis with claim development tracking, cross-foot validation, and pattern detection.
      </p>

      {/* Document summary */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
          Documents to Analyze ({documents?.length || 0})
        </h4>
        {documents && documents.length > 0 ? (
          <div className="space-y-1">
            {documents.map((doc: any, index: number) => (
              <p key={doc.id || index} className="text-sm text-blue-700 dark:text-blue-300">
                • {doc.name || doc.fileName || 'Document ' + (index + 1)}
              </p>
            ))}
          </div>
        ) : (
          <p className="text-sm text-blue-600 dark:text-blue-400">No documents selected</p>
        )}
        {documents && documents.length > 3 && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
            ⚠️ Optimal analysis: 3 documents. Processing {documents.length} may take longer.
          </p>
        )}
        {pastReports && pastReports.length > 0 && (
          <div className="mt-2 pt-2 border-t border-blue-300 dark:border-blue-700">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              + {pastReports.length} past report{pastReports.length !== 1 ? 's' : ''} for context
            </p>
          </div>
        )}
      </div>

      {/* Additional context if provided */}
      {additionalContext && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Additional Context</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">{additionalContext}</p>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">
            {error.userMessage}
          </h4>
          <p className="text-sm text-red-700 dark:text-red-300">
            {error.technicalMessage}
          </p>
          {error.isRetryable && analysisDepth && (
            <button
              onClick={() => handleAnalyze(analysisDepth)}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Retry Analysis
            </button>
          )}
        </div>
      )}

      {/* Progress display */}
      {isAnalyzing && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                Forensic Loss Run Analysis in Progress
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                {progressMessage}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                Processing {documents.length} loss run document{documents.length !== 1 ? 's' : ''} with cross-document reconciliation...
              </p>
            </div>
          </div>

          {/* Real-time progress bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-blue-700 dark:text-blue-300 mb-2">
              <span>{progress}% complete</span>
              <span>{progress < 100 ? 'Processing...' : 'Done!'}</span>
            </div>
            <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Execute buttons */}
      {!isAnalyzing && !result && (
        <div className="space-y-4">
          {/* Report Title Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Report Title (Optional)
            </label>
            <input
              type="text"
              value={reportTitle || ''}
              onChange={(e) => onReportTitleChange?.(e.target.value)}
              placeholder="Leave blank to auto-generate title"
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-blue-500
                       dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Customize the report title or leave blank to use the default format
            </p>
          </div>

          {/* Analysis mode selection */}
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleAnalyze('quick')}
              disabled={loading || !documents || documents.length === 0}
              className="flex flex-col items-center px-6 py-4 border-2 border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:border-blue-400 dark:hover:border-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-6 h-6 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="font-semibold text-sm">Quick Analysis</span>
              <span className="text-xs mt-1 opacity-75">Faster results, ideal for initial review</span>
            </button>
            <button
              onClick={() => handleAnalyze('deep')}
              disabled={loading || !documents || documents.length === 0}
              className="flex flex-col items-center px-6 py-4 border-2 border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/40 hover:border-purple-400 dark:hover:border-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-6 h-6 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="font-semibold text-sm">Deep Analysis</span>
              <span className="text-xs mt-1 opacity-75">Thorough forensic analysis, takes longer</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const FinancialAnalysis: React.FC<{
  documents: any[];
  additionalContext: string;
  clientId: string;
  projectId?: string;
  onComplete: (result: any) => void;
  onError?: (error: string) => void;
  onExecutionStart?: (workflowInfo: { workflowType: string; documents: any[] }) => void;
  onJobQueued?: (jobId: string) => void;
  token?: string | null;
  loading: boolean;
  reportTitle?: string;
  onReportTitleChange?: (title: string) => void;
}> = ({ documents, additionalContext, clientId, projectId, onComplete, onError, onExecutionStart, onJobQueued, token, loading, reportTitle, onReportTitleChange }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Initializing...');
  const [error, setError] = useState<CategorizedError | null>(null);

  // Listen for progress updates
  useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      const { progress: newProgress, status } = event.detail;
      devLog('📊 Financial Analysis Progress update received:', { progress: newProgress, status });
      setProgress(newProgress);
      setProgressMessage(getProgressMessage(newProgress));
    };

    window.addEventListener('financialAnalysisProgress' as any, handleProgressUpdate);

    return () => {
      window.removeEventListener('financialAnalysisProgress' as any, handleProgressUpdate);
    };
  }, []);

  const getProgressMessage = (progress: number): string => {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading financial documents...';
    if (progress < 30) return 'Preparing documents...';
    if (progress < 40) return 'Generating analysis prompts...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 70) return 'AI analyzing financial statements... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations and risk assessment...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  };

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      setProgress(0);
      setProgressMessage('Starting analysis...');
      setError(null);

      // Notify parent that execution is starting (for background timer)
      if (onExecutionStart) {
        onExecutionStart({ workflowType: 'financial_analysis', documents });
      }

      // Extract document IDs for job queue
      const documentIds = documents?.map((d: any) => d.id) || [];

      devLog('🔄 FinancialAnalysis: Starting analysis with:', {
        documentsCount: documents?.length,
        additionalContext,
        clientId,
        projectId,
        documentIds
      });

      // Try to submit to job queue first (if token available)
      if (token && onJobQueued) {
        try {
          devLog('📤 FinancialAnalysis: Submitting to job queue...');

          const jobResponse = await jobsService.submitJob(token, {
            workflowType: 'financial_analysis',
            customerId: clientId,
            projectId: projectId,
            documents: documentIds,
            additionalContext: additionalContext,
            reportTitle: reportTitle || undefined,
          });

          if (jobResponse.success) {
            devLog(`✅ FinancialAnalysis: Job queued successfully: ${jobResponse.jobId}`);
            setIsAnalyzing(false);
            onJobQueued(jobResponse.jobId);
            return; // Exit early - job is queued
          }
        } catch (queueError) {
          // Job queue not available, fall back to synchronous execution
          devLog('⚠️ FinancialAnalysis: Job queue not available, falling back to synchronous execution:', queueError);
        }
      }

      // FALLBACK: Synchronous execution
      const analysisResult = await workflowService.executeFinancialAnalysis({
        documents,
        additionalContext,
        clientId,
        projectId,
        reportTitle: reportTitle || undefined
      });

      devLog('✅ FinancialAnalysis: Analysis completed:', {
        hasResult: !!analysisResult,
        resultId: analysisResult?.id,
        hasContent: !!analysisResult?.content
      });

      setResult(analysisResult);
      setProgress(100);
      setProgressMessage('Complete!');
      onComplete(analysisResult);
    } catch (err) {
      console.error('❌ FinancialAnalysis: Error analyzing financial documents:', err);
      const categorized = categorizeError(err);
      setError(categorized);
      setProgressMessage(categorized.userMessage);
      if (onError) {
        onError(categorized.technicalMessage);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Financial Analysis</h3>

      {isAnalyzing && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                Analyzing Financial Documents
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                {progressMessage}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                Processing {documents.length} document{documents.length !== 1 ? 's' : ''} for financial analysis...
              </p>
            </div>
          </div>

          {/* Real-time progress bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-blue-700 dark:text-blue-300 mb-2">
              <span>{progress}% complete</span>
              <span>{progress < 100 ? 'Processing...' : 'Done!'}</span>
            </div>
            <div className="bg-blue-200 dark:bg-blue-800 rounded-full h-3 overflow-hidden">
              <div
                className="bg-blue-500 dark:bg-blue-400 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error display with retry button */}
      {error && !isAnalyzing && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-red-800 dark:text-red-200">
                {error.userMessage}
              </h4>
              {error.suggestedAction && (
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {error.suggestedAction}
                </p>
              )}
              <details className="mt-2">
                <summary className="text-xs text-red-600 dark:text-red-400 cursor-pointer hover:underline">
                  Technical details
                </summary>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-mono bg-red-100 dark:bg-red-900/40 p-2 rounded">
                  {error.technicalMessage}
                </p>
              </details>
              {error.isRetryable && (
                <button
                  onClick={() => {
                    setError(null);
                    handleAnalyze();
                  }}
                  className="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                >
                  Retry Analysis
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!result && !isAnalyzing && !error && (
        <div className="space-y-4">
          {/* Report Title Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Report Title (Optional)
            </label>
            <input
              type="text"
              value={reportTitle || ''}
              onChange={(e) => onReportTitleChange?.(e.target.value)}
              placeholder="Leave blank to auto-generate title"
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-blue-500
                       dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Customize the report title or leave blank to use the default format
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
            >
              Execute Financial Analysis
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
            Financial Analysis Complete
          </h4>
          <p className="text-sm text-green-700 dark:text-green-300">
            {result.summary || 'Financial analysis completed successfully'}
          </p>
        </div>
      )}
    </div>
  );
};

export const ClientRenewalPresentation: React.FC<{
  documents: any[];
  additionalContext: string;
  clientId: string;
  projectId?: string;
  onComplete: (result: any) => void;
  onError?: (error: string) => void;
  onExecutionStart?: (workflowInfo: { workflowType: string; documents: any[] }) => void;
  onJobQueued?: (jobId: string) => void;
  token?: string | null;
  loading: boolean;
  reportTitle?: string;
  onReportTitleChange?: (title: string) => void;
}> = ({ documents, additionalContext, clientId, projectId, onComplete, onError, onExecutionStart, onJobQueued, token, loading, reportTitle, onReportTitleChange }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Initializing...');
  const [error, setError] = useState<CategorizedError | null>(null);

  // Listen for progress updates
  useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      const { progress: newProgress, status } = event.detail;
      devLog('📊 Client Renewal Presentation Progress update received:', { progress: newProgress, status });
      setProgress(newProgress);
      setProgressMessage(getProgressMessage(newProgress));
    };

    window.addEventListener('clientRenewalPresentationProgress' as any, handleProgressUpdate);

    return () => {
      window.removeEventListener('clientRenewalPresentationProgress' as any, handleProgressUpdate);
    };
  }, []);

  const getProgressMessage = (progress: number): string => {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading insurance documents...';
    if (progress < 30) return 'Preparing documents...';
    if (progress < 40) return 'Generating analysis prompts...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 70) return 'AI analyzing renewal documents... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations and coverage analysis...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  };

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      setProgress(0);
      setProgressMessage('Starting analysis...');
      setError(null);

      // Notify parent that execution is starting (for background timer)
      if (onExecutionStart) {
        onExecutionStart({ workflowType: 'client_renewal_presentation', documents });
      }

      // Extract document IDs for job queue
      const documentIds = documents?.map((d: any) => d.id) || [];

      devLog('🔄 ClientRenewalPresentation: Starting analysis with:', {
        documentsCount: documents?.length,
        additionalContext,
        clientId,
        projectId,
        documentIds
      });

      // Try to submit to job queue first (if token available)
      if (token && onJobQueued) {
        try {
          devLog('📤 ClientRenewalPresentation: Submitting to job queue...');

          const jobResponse = await jobsService.submitJob(token, {
            workflowType: 'client_renewal_presentation',
            customerId: clientId,
            projectId: projectId,
            documents: documentIds,
            additionalContext: additionalContext,
            reportTitle: reportTitle || undefined,
          });

          if (jobResponse.success) {
            devLog(`✅ ClientRenewalPresentation: Job queued successfully: ${jobResponse.jobId}`);
            setIsAnalyzing(false);
            onJobQueued(jobResponse.jobId);
            return; // Exit early - job is queued
          }
        } catch (queueError) {
          // Job queue not available, fall back to synchronous execution
          devLog('⚠️ ClientRenewalPresentation: Job queue not available, falling back to synchronous execution:', queueError);
        }
      }

      // FALLBACK: Synchronous execution
      const analysisResult = await workflowService.executeClientRenewalPresentation({
        documents,
        additionalContext,
        clientId,
        projectId,
        reportTitle: reportTitle || undefined
      });

      devLog('✅ ClientRenewalPresentation: Analysis completed:', {
        hasResult: !!analysisResult,
        resultId: analysisResult?.id,
        hasContent: !!analysisResult?.content
      });

      setResult(analysisResult);
      setProgress(100);
      setProgressMessage('Complete!');
      onComplete(analysisResult);
    } catch (err) {
      console.error('❌ ClientRenewalPresentation: Error generating presentation:', err);
      const categorized = categorizeError(err);
      setError(categorized);
      setProgressMessage(categorized.userMessage);
      if (onError) {
        onError(categorized.technicalMessage);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Client Renewal Presentation</h3>

      {isAnalyzing && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                Generating Client Renewal Presentation
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                {progressMessage}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                Processing {documents.length} document{documents.length !== 1 ? 's' : ''} for renewal presentation...
              </p>
            </div>
          </div>

          {/* Real-time progress bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-blue-700 dark:text-blue-300 mb-2">
              <span>{progress}% complete</span>
              <span>{progress < 100 ? 'Processing...' : 'Done!'}</span>
            </div>
            <div className="bg-blue-200 dark:bg-blue-800 rounded-full h-3 overflow-hidden">
              <div
                className="bg-blue-500 dark:bg-blue-400 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error display with retry button */}
      {error && !isAnalyzing && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-red-800 dark:text-red-200">
                {error.userMessage}
              </h4>
              {error.suggestedAction && (
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {error.suggestedAction}
                </p>
              )}
              <details className="mt-2">
                <summary className="text-xs text-red-600 dark:text-red-400 cursor-pointer hover:underline">
                  Technical details
                </summary>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-mono bg-red-100 dark:bg-red-900/40 p-2 rounded">
                  {error.technicalMessage}
                </p>
              </details>
              {error.isRetryable && (
                <button
                  onClick={() => {
                    setError(null);
                    handleAnalyze();
                  }}
                  className="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                >
                  Retry Analysis
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!result && !isAnalyzing && !error && (
        <div className="space-y-4">
          {/* Report Title Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Report Title (Optional)
            </label>
            <input
              type="text"
              value={reportTitle || ''}
              onChange={(e) => onReportTitleChange?.(e.target.value)}
              placeholder="Leave blank to auto-generate title"
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-blue-500
                       dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Customize the report title or leave blank to use the default format
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
            >
              Generate Client Renewal Presentation
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
            Client Renewal Presentation Complete
          </h4>
          <p className="text-sm text-green-700 dark:text-green-300">
            {result.summary || 'Client renewal presentation generated successfully'}
          </p>
        </div>
      )}
    </div>
  );
};

// ===== RATE ANALYSIS EXECUTION COMPONENT =====
export const RateAnalysisExecution: React.FC<{
  documents: any[];
  analysisTier: string;
  additionalContext?: string;
  clientId: string;
  clientName?: string;
  projectId?: string;
  onComplete: (result: any) => void;
  onError?: (error: string) => void;
  onExecutionStart?: (workflowInfo: { workflowType: string; documents: any[] }) => void;
  onJobQueued?: (jobId: string) => void;
  token?: string | null;
  loading: boolean;
  reportTitle?: string;
  onReportTitleChange?: (title: string) => void;
}> = ({ documents, analysisTier, additionalContext, clientId, clientName, projectId, onComplete, onError, onExecutionStart, onJobQueued, token, loading, reportTitle, onReportTitleChange }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Initializing...');
  const [error, setError] = useState<CategorizedError | null>(null);

  // Listen for progress updates
  useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      const { progress: newProgress, status } = event.detail;
      devLog('📊 Rate Analysis Progress update received:', { progress: newProgress, status });
      setProgress(newProgress);
      setProgressMessage(getProgressMessage(newProgress));
    };

    window.addEventListener('rateAnalysisProgress' as any, handleProgressUpdate);

    return () => {
      window.removeEventListener('rateAnalysisProgress' as any, handleProgressUpdate);
    };
  }, []);

  const getTierDisplayName = (tier: string): string => {
    const tierNames: Record<string, string> = {
      'tier1': 'Tier 1: Rate & Classification Analysis',
      'tier2': 'Tier 2: Rating Basis Analysis',
      'tier3': 'Tier 3: Integrated Analysis',
      'tier4': 'Tier 4: Temporal Pattern Recognition',
    };
    return tierNames[tier] || tier;
  };

  const getProgressMessage = (progress: number): string => {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading documents...';
    if (progress < 30) return 'Extracting rating data...';
    if (progress < 40) return 'Preparing premium decomposition...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 70) return 'AI analyzing rate components... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  };

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      setProgress(0);
      setProgressMessage('Starting rate analysis...');
      setError(null);

      // Notify parent that execution is starting (for background timer)
      if (onExecutionStart) {
        onExecutionStart({ workflowType: 'rate_analysis', documents });
      }

      // Extract document IDs for job queue
      const documentIds = documents?.map((d: any) => d.id) || [];

      devLog('🔄 RateAnalysisExecution: Starting analysis with:', {
        documentsCount: documents?.length,
        analysisTier,
        additionalContext,
        clientId,
        projectId,
        documentIds
      });

      // Try to submit to job queue first (if token available)
      if (token && onJobQueued) {
        try {
          devLog('📤 RateAnalysisExecution: Submitting to job queue...');

          const jobResponse = await jobsService.submitJob(token, {
            workflowType: 'rate_analysis',
            customerId: clientId,
            projectId: projectId,
            documents: documentIds,
            additionalContext: additionalContext,
            analysisMode: analysisTier, // Pass analysis tier as analysisMode for WorkflowExecutor mapping
            reportTitle: reportTitle || undefined,
          });

          if (jobResponse.success) {
            devLog(`✅ RateAnalysisExecution: Job queued successfully: ${jobResponse.jobId}`);
            setIsAnalyzing(false);
            onJobQueued(jobResponse.jobId);
            return; // Exit early - job is queued
          }
        } catch (queueError) {
          // Job queue not available, fall back to synchronous execution
          devLog('⚠️ RateAnalysisExecution: Job queue not available, falling back to synchronous execution:', queueError);
        }
      }

      // FALLBACK: Synchronous execution
      const analysisResult = await workflowService.executeRateAnalysis({
        documents,
        analysisTier,
        additionalInfo: additionalContext,
        clientId,
        projectId,
        reportTitle: reportTitle || undefined
      });

      devLog('✅ RateAnalysisExecution: Analysis completed:', {
        hasResult: !!analysisResult,
        resultId: analysisResult?.id,
        hasContent: !!analysisResult?.content
      });

      setResult(analysisResult);
      setProgress(100);
      setProgressMessage('Complete!');
      onComplete(analysisResult);
    } catch (err) {
      console.error('❌ RateAnalysisExecution: Error analyzing documents:', err);
      const categorized = categorizeError(err);
      setError(categorized);
      setProgressMessage(categorized.userMessage);
      if (onError) {
        onError(categorized.technicalMessage);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Rate Analysis</h3>

      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <strong>Analysis Tier:</strong> {getTierDisplayName(analysisTier)}
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          <strong>Documents:</strong> {documents.length} document{documents.length !== 1 ? 's' : ''} selected
        </p>
      </div>

      {isAnalyzing && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                Performing Rate Analysis
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                {progressMessage}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                Analyzing {documents.length} document{documents.length !== 1 ? 's' : ''} for premium decomposition...
              </p>
            </div>
          </div>
          {/* Real-time progress bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-blue-700 dark:text-blue-300 mb-2">
              <span>{progress}% complete</span>
              <span>{progress < 100 ? 'Processing...' : 'Done!'}</span>
            </div>
            <div className="bg-blue-200 dark:bg-blue-800 rounded-full h-3 overflow-hidden">
              <div
                className="bg-blue-500 dark:bg-blue-400 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error display with retry button */}
      {error && !isAnalyzing && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-red-800 dark:text-red-200">
                {error.userMessage}
              </h4>
              {error.suggestedAction && (
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {error.suggestedAction}
                </p>
              )}
              <details className="mt-2">
                <summary className="text-xs text-red-600 dark:text-red-400 cursor-pointer hover:underline">
                  Technical details
                </summary>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-mono bg-red-100 dark:bg-red-900/40 p-2 rounded">
                  {error.technicalMessage}
                </p>
              </details>
              {error.isRetryable && (
                <button
                  onClick={() => {
                    setError(null);
                    handleAnalyze();
                  }}
                  className="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                >
                  Retry Analysis
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!result && !isAnalyzing && !error && (
        <div className="space-y-4">
          {/* Report Title Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Report Title (Optional)
            </label>
            <input
              type="text"
              value={reportTitle || ''}
              onChange={(e) => onReportTitleChange?.(e.target.value)}
              placeholder="Leave blank to auto-generate title"
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-blue-500
                       dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Customize the report title or leave blank to use the default format
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
            >
              Execute Rate Analysis
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
            Rate Analysis Complete
          </h4>
          <p className="text-sm text-green-700 dark:text-green-300">
            {result.summary || 'Rate analysis completed successfully'}
          </p>
        </div>
      )}
    </div>
  );
};

// ===== PORTFOLIO ANALYZER EXECUTION COMPONENT =====
export const PortfolioAnalyzerExecution: React.FC<{
  documents: any[];
  scenario?: string;
  additionalContext?: string;
  clientId: string;
  clientName?: string;
  projectId?: string;
  onComplete: (result: any) => void;
  onError?: (error: string) => void;
  onExecutionStart?: (workflowInfo: { workflowType: string; documents: any[] }) => void;
  onJobQueued?: (jobId: string) => void;
  token?: string | null;
  loading: boolean;
  reportTitle?: string;
  onReportTitleChange?: (title: string) => void;
}> = ({ documents, scenario, additionalContext, clientId, clientName, projectId, onComplete, onError, onExecutionStart, onJobQueued, token, loading, reportTitle, onReportTitleChange }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Initializing...');
  const [error, setError] = useState<CategorizedError | null>(null);

  // Listen for progress updates
  useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      const { progress: newProgress, status } = event.detail;
      devLog('📊 Portfolio Analyzer Progress update received:', { progress: newProgress, status });
      setProgress(newProgress);
      setProgressMessage(getProgressMessage(newProgress));
    };

    window.addEventListener('portfolioAnalyzerProgress' as any, handleProgressUpdate);

    return () => {
      window.removeEventListener('portfolioAnalyzerProgress' as any, handleProgressUpdate);
    };
  }, []);

  const getProgressMessage = (progress: number): string => {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading policy documents...';
    if (progress < 30) return 'Mapping portfolio structure...';
    if (progress < 40) return 'Decomposing scenario into components...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 60) return 'Performing vertical layer analysis...';
    if (progress < 70) return 'Performing horizontal coverage analysis... (this may take a few minutes)';
    if (progress < 80) return 'Analyzing gaps and overlaps...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  };

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      setProgress(0);
      setProgressMessage('Starting portfolio scenario analysis...');
      setError(null);

      // Notify parent that execution is starting (for background timer)
      if (onExecutionStart) {
        onExecutionStart({ workflowType: 'portfolio_analyzer', documents });
      }

      // Extract document IDs for job queue
      const documentIds = documents?.map((d: any) => d.id) || [];

      devLog('🔄 PortfolioAnalyzerExecution: Starting analysis with:', {
        documentsCount: documents?.length,
        scenario,
        additionalContext,
        clientId,
        projectId,
        documentIds
      });

      // Try to submit to job queue first (if token available)
      if (token && onJobQueued) {
        try {
          devLog('📤 PortfolioAnalyzerExecution: Submitting to job queue...');

          const jobResponse = await jobsService.submitJob(token, {
            workflowType: 'portfolio_analyzer',
            customerId: clientId,
            projectId: projectId,
            documents: documentIds,
            additionalContext: additionalContext,
            context: scenario, // Pass scenario as context
            reportTitle: reportTitle || undefined,
          });

          if (jobResponse.success) {
            devLog(`✅ PortfolioAnalyzerExecution: Job queued successfully: ${jobResponse.jobId}`);
            setIsAnalyzing(false);
            onJobQueued(jobResponse.jobId);
            return; // Exit early - job is queued
          }
        } catch (queueError) {
          // Job queue not available, fall back to synchronous execution
          devLog('⚠️ PortfolioAnalyzerExecution: Job queue not available, falling back to synchronous execution:', queueError);
        }
      }

      // FALLBACK: Synchronous execution
      const analysisResult = await workflowService.executePortfolioAnalyzer({
        documents,
        scenario,
        additionalInfo: additionalContext,
        clientId,
        projectId,
        reportTitle: reportTitle || undefined
      });

      devLog('✅ PortfolioAnalyzerExecution: Analysis completed:', {
        hasResult: !!analysisResult,
        resultId: analysisResult?.id,
        hasContent: !!analysisResult?.content
      });

      setResult(analysisResult);
      setProgress(100);
      setProgressMessage('Complete!');
      onComplete(analysisResult);
    } catch (err) {
      console.error('❌ PortfolioAnalyzerExecution: Error analyzing documents:', err);
      const categorized = categorizeError(err);
      setError(categorized);
      setProgressMessage(categorized.userMessage);
      if (onError) {
        onError(categorized.technicalMessage);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Portfolio Scenario Analysis</h3>

      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <strong>Documents:</strong> {documents.length} policy document{documents.length !== 1 ? 's' : ''} selected
        </p>
        {scenario && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            <strong>Scenario:</strong> {scenario.length > 100 ? scenario.substring(0, 100) + '...' : scenario}
          </p>
        )}
      </div>

      {isAnalyzing && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                Performing Portfolio Scenario Analysis
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">
                {progressMessage}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                Analyzing {documents.length} document{documents.length !== 1 ? 's' : ''} for multi-policy coverage coordination...
              </p>
            </div>
          </div>
          {/* Real-time progress bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-blue-700 dark:text-blue-300 mb-2">
              <span>{progress}% complete</span>
              <span>{progress < 100 ? 'Processing...' : 'Done!'}</span>
            </div>
            <div className="bg-blue-200 dark:bg-blue-800 rounded-full h-3 overflow-hidden">
              <div
                className="bg-blue-500 dark:bg-blue-400 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error display with retry button */}
      {error && !isAnalyzing && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-red-800 dark:text-red-200">
                {error.userMessage}
              </h4>
              {error.suggestedAction && (
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {error.suggestedAction}
                </p>
              )}
              <details className="mt-2">
                <summary className="text-xs text-red-600 dark:text-red-400 cursor-pointer hover:underline">
                  Technical details
                </summary>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-mono bg-red-100 dark:bg-red-900/40 p-2 rounded">
                  {error.technicalMessage}
                </p>
              </details>
              {error.isRetryable && (
                <button
                  onClick={() => {
                    setError(null);
                    handleAnalyze();
                  }}
                  className="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                >
                  Retry Analysis
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!result && !isAnalyzing && !error && (
        <div className="space-y-4">
          {/* Report Title Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Report Title (Optional)
            </label>
            <input
              type="text"
              value={reportTitle || ''}
              onChange={(e) => onReportTitleChange?.(e.target.value)}
              placeholder="Leave blank to auto-generate title"
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-blue-500
                       dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Customize the report title or leave blank to use the default format
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
            >
              Execute Portfolio Analysis
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
            Portfolio Analysis Complete
          </h4>
          <p className="text-sm text-green-700 dark:text-green-300">
            {result.summary || 'Portfolio scenario analysis completed successfully'}
          </p>
        </div>
      )}
    </div>
  );
};

// ===== HOMEOWNERS INSURANCE COMPARISON EXECUTION COMPONENT =====
export const HomeownersComparisonExecution: React.FC<{
  documents: any[];
  additionalContext?: string;
  clientId: string;
  clientName?: string;
  projectId?: string;
  onComplete: (result: any) => void;
  onError?: (error: string) => void;
  onExecutionStart?: (workflowInfo: { workflowType: string; documents: any[] }) => void;
  onJobQueued?: (jobId: string) => void;
  token?: string | null;
  loading: boolean;
  reportTitle?: string;
  onReportTitleChange?: (title: string) => void;
}> = ({ documents, additionalContext, clientId, clientName, projectId, onComplete, onError, onExecutionStart, onJobQueued, token, loading, reportTitle, onReportTitleChange }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Initializing...');
  const [error, setError] = useState<CategorizedError | null>(null);

  // Listen for progress updates
  useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      const { progress: newProgress, status } = event.detail;
      devLog('🏠 Homeowners Comparison Progress update received:', { progress: newProgress, status });
      setProgress(newProgress);
      setProgressMessage(getProgressMessage(newProgress));
    };

    window.addEventListener('homeownersComparisonProgress' as any, handleProgressUpdate);

    return () => {
      window.removeEventListener('homeownersComparisonProgress' as any, handleProgressUpdate);
    };
  }, []);

  const getProgressMessage = (progress: number): string => {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading insurance documents...';
    if (progress < 30) return 'Classifying documents and determining analysis mode...';
    if (progress < 40) return 'Performing temporal verification...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 60) return 'Extracting coverage data and building exposure profile... (this may take a few minutes)';
    if (progress < 70) return 'Analyzing coverage gaps and compound failures...';
    if (progress < 80) return 'Generating decision framework and recommendations...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  };

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      setProgress(0);
      setProgressMessage('Starting homeowners insurance comparison...');
      setError(null);

      // Notify parent that execution is starting (for background timer)
      if (onExecutionStart) {
        onExecutionStart({ workflowType: 'homeowners_comparison', documents });
      }

      // Extract document IDs for job queue
      const documentIds = documents?.map((d: any) => d.id) || [];

      devLog('🔄 HomeownersComparisonExecution: Starting analysis with:', {
        documentsCount: documents?.length,
        additionalContext,
        clientId,
        projectId,
        documentIds
      });

      // Try to submit to job queue first (if token available)
      if (token && onJobQueued) {
        try {
          devLog('📤 HomeownersComparisonExecution: Submitting to job queue...');

          const jobResponse = await jobsService.submitJob(token, {
            workflowType: 'homeowners_comparison',
            customerId: clientId,
            projectId: projectId,
            documents: documentIds,
            additionalContext: additionalContext,
            reportTitle: reportTitle || undefined,
          });

          if (jobResponse.success) {
            devLog(`✅ HomeownersComparisonExecution: Job queued successfully: ${jobResponse.jobId}`);
            setIsAnalyzing(false);
            onJobQueued(jobResponse.jobId);
            return; // Exit early - job is queued
          }
        } catch (queueError) {
          // Job queue not available, fall back to synchronous execution
          devLog('⚠️ HomeownersComparisonExecution: Job queue not available, falling back to synchronous execution:', queueError);
        }
      }

      // FALLBACK: Synchronous execution
      const analysisResult = await workflowService.executeHomeownersComparison({
        documents,
        additionalInfo: additionalContext,
        clientId,
        projectId,
        reportTitle: reportTitle || undefined
      });

      devLog('✅ HomeownersComparisonExecution: Analysis completed:', {
        hasResult: !!analysisResult,
        resultId: analysisResult?.id,
        hasContent: !!analysisResult?.content
      });

      setResult(analysisResult);
      setProgress(100);
      setProgressMessage('Complete!');
      onComplete(analysisResult);
    } catch (err) {
      console.error('❌ HomeownersComparisonExecution: Error analyzing documents:', err);
      const categorized = categorizeError(err);
      setError(categorized);
      setProgressMessage(categorized.userMessage);
      if (onError) {
        onError(categorized.technicalMessage);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Homeowners Insurance Comparison</h3>

      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <strong>Documents:</strong> {documents.length} document{documents.length !== 1 ? 's' : ''} selected (max 3)
        </p>
        {additionalContext && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            <strong>Additional Context:</strong> {additionalContext.length > 100 ? additionalContext.substring(0, 100) + '...' : additionalContext}
          </p>
        )}
      </div>

      {isAnalyzing && (
        <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            <div className="flex-1">
              <h4 className="font-medium text-teal-800 dark:text-teal-200 mb-1">
                Performing Homeowners Insurance Comparison
              </h4>
              <p className="text-sm text-teal-700 dark:text-teal-300 mb-2">
                {progressMessage}
              </p>
              <p className="text-xs text-teal-600 dark:text-teal-400">
                Analyzing {documents.length} document{documents.length !== 1 ? 's' : ''} for coverage gaps, compound failures, and decision recommendations...
              </p>
            </div>
          </div>
          {/* Real-time progress bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-teal-700 dark:text-teal-300 mb-2">
              <span>{progress}% complete</span>
              <span>{progress < 100 ? 'Processing...' : 'Done!'}</span>
            </div>
            <div className="bg-teal-200 dark:bg-teal-800 rounded-full h-3 overflow-hidden">
              <div
                className="bg-teal-500 dark:bg-teal-400 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error display with retry button */}
      {error && !isAnalyzing && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-red-800 dark:text-red-200">
                {error.userMessage}
              </h4>
              {error.suggestedAction && (
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {error.suggestedAction}
                </p>
              )}
              <button
                onClick={handleAnalyze}
                className="mt-3 px-4 py-2 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded-lg hover:bg-red-200 dark:hover:bg-red-700 text-sm font-medium"
              >
                Retry Analysis
              </button>
            </div>
          </div>
        </div>
      )}

      {!isAnalyzing && !result && !error && (
        <div className="space-y-4">
          {/* Report title input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Report Title (Optional)
            </label>
            <input
              type="text"
              value={reportTitle || ''}
              onChange={(e) => onReportTitleChange?.(e.target.value)}
              placeholder="Custom report title (leave blank for default)"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white
                       dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Customize the report title or leave blank to use the default format
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
            >
              Execute Homeowners Comparison
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
            Homeowners Insurance Comparison Complete
          </h4>
          <p className="text-sm text-green-700 dark:text-green-300">
            {result.summary || 'Homeowners insurance comparison analysis completed successfully'}
          </p>
        </div>
      )}
    </div>
  );
};

// ===== PERSONAL UMBRELLA ANALYSIS EXECUTION COMPONENT =====
export const PersonalUmbrellaExecution: React.FC<{
  documents: any[];
  additionalContext?: string;
  questionnaireData?: Record<string, any>;
  clientId: string;
  clientName?: string;
  projectId?: string;
  onComplete: (result: any) => void;
  onError?: (error: string) => void;
  onExecutionStart?: (workflowInfo: { workflowType: string; documents: any[] }) => void;
  onJobQueued?: (jobId: string) => void;
  token?: string | null;
  loading: boolean;
  reportTitle?: string;
  onReportTitleChange?: (title: string) => void;
}> = ({ documents, additionalContext, questionnaireData, clientId, clientName, projectId, onComplete, onError, onExecutionStart, onJobQueued, token, loading, reportTitle, onReportTitleChange }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Initializing...');
  const [error, setError] = useState<CategorizedError | null>(null);

  useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      const { progress: newProgress } = event.detail;
      setProgress(newProgress);
      setProgressMessage(getProgressMessage(newProgress));
    };
    window.addEventListener('personalUmbrellaProgress' as any, handleProgressUpdate);
    return () => {
      window.removeEventListener('personalUmbrellaProgress' as any, handleProgressUpdate);
    };
  }, []);

  const getProgressMessage = (progress: number): string => {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading umbrella and underlying documents...';
    if (progress < 30) return 'Classifying documents and determining track/mode...';
    if (progress < 40) return 'Verifying temporal data and policy architecture...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 60) return 'Extracting exclusions and building drop-down matrix... (this may take a few minutes)';
    if (progress < 70) return 'Scanning compound failure patterns and exposure mapping...';
    if (progress < 80) return 'Generating gap synthesis and decision framework...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  };

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      setProgress(0);
      setProgressMessage('Starting personal umbrella analysis...');
      setError(null);

      if (onExecutionStart) {
        onExecutionStart({ workflowType: 'personal_umbrella', documents });
      }

      const documentIds = documents?.map((d: any) => d.id) || [];

      let context = additionalContext || '';
      if (questionnaireData && Object.keys(questionnaireData).length > 0) {
        context = JSON.stringify({ questionnaire: questionnaireData, additionalContext: additionalContext || '' });
      }

      if (token && onJobQueued) {
        try {
          const jobResponse = await jobsService.submitJob(token, {
            workflowType: 'personal_umbrella',
            customerId: clientId,
            projectId: projectId,
            documents: documentIds,
            additionalContext: context,
            reportTitle: reportTitle || undefined,
          });

          if (jobResponse.success) {
            setIsAnalyzing(false);
            onJobQueued(jobResponse.jobId);
            return;
          }
        } catch (queueError) {
          devLog('⚠️ PersonalUmbrellaExecution: Job queue not available:', queueError);
        }
      }

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/workflows/unified/execute/personal_umbrella`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          documents: documentIds,
          customerId: clientId,
          projectId: projectId,
          additionalContext: context,
          reportTitle: reportTitle || undefined,
        }),
      });

      if (!response.ok) throw new Error(`Analysis failed: ${response.statusText}`);

      const analysisResult = await response.json();
      setResult(analysisResult);
      setProgress(100);
      setProgressMessage('Complete!');
      onComplete(analysisResult);
    } catch (err) {
      const categorized = categorizeError(err);
      setError(categorized);
      setProgressMessage(categorized.userMessage);
      if (onError) onError(categorized.technicalMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Personal Umbrella Analysis</h3>

      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <strong>Documents:</strong> {documents.length} document{documents.length !== 1 ? 's' : ''} selected
        </p>
        {questionnaireData && Object.keys(questionnaireData).length > 0 ? (
          <p className="text-sm text-green-600 dark:text-green-400 mt-1">
            <strong>Track 1 + Track 2:</strong> Exposure questionnaire provided — full exposure mapping enabled.
          </p>
        ) : (
          <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
            <strong>Track 1 Only:</strong> No questionnaire — structural analysis only.
          </p>
        )}
        {additionalContext && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            <strong>Context:</strong> {additionalContext.length > 100 ? additionalContext.substring(0, 100) + '...' : additionalContext}
          </p>
        )}
      </div>

      {isAnalyzing && (
        <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <div className="flex-1">
              <h4 className="font-medium text-indigo-800 dark:text-indigo-200 mb-1">Performing Personal Umbrella Analysis</h4>
              <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-2">{progressMessage}</p>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-sm text-indigo-700 dark:text-indigo-300 mb-2">
              <span>{progress}% complete</span>
              <span>{progress < 100 ? 'Processing...' : 'Done!'}</span>
            </div>
            <div className="bg-indigo-200 dark:bg-indigo-800 rounded-full h-3 overflow-hidden">
              <div className="bg-indigo-500 dark:bg-indigo-400 h-3 rounded-full transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      )}

      {error && !isAnalyzing && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-1">
              <h4 className="font-medium text-red-800 dark:text-red-200">{error.userMessage}</h4>
              {error.suggestedAction && <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error.suggestedAction}</p>}
              <button onClick={handleAnalyze} className="mt-3 px-4 py-2 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded-lg hover:bg-red-200 dark:hover:bg-red-700 text-sm font-medium">Retry Analysis</button>
            </div>
          </div>
        </div>
      )}

      {!isAnalyzing && !result && !error && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Report Title (Optional)</label>
            <input type="text" value={reportTitle || ''} onChange={(e) => onReportTitleChange?.(e.target.value)} placeholder="Custom report title (leave blank for default)" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" />
          </div>
          <div className="text-center">
            <button onClick={handleAnalyze} disabled={isAnalyzing} className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50">Execute Umbrella Analysis</button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">Personal Umbrella Analysis Complete</h4>
          <p className="text-sm text-green-700 dark:text-green-300">{result.summary || 'Personal umbrella analysis completed successfully'}</p>
        </div>
      )}
    </div>
  );
};

// ===== CYBER BETA SUITE — GENERIC EXECUTION COMPONENT =====
// Shared by all 5 cyber_* workflow types (cyber_auto, cyber_benchmark,
// cyber_renewal_delta, cyber_war_systemic, cyber_ransomware_trap) — they all
// follow the same doc-selection -> optional-context -> run flow, so one
// parameterized component replaces 5 bespoke ones. See
// CYBER_BETA_TOOLS_PLAN.md Phase 2.
const CYBER_TOOL_DISPLAY_NAMES: Record<string, string> = {
  cyber_auto: 'Cyber (Automatic)',
  cyber_benchmark: 'Cyber Policy Benchmark',
  cyber_renewal_delta: 'Cyber Renewal Delta (YOY)',
  cyber_war_systemic: 'Cyber War & Systemic Exclusion Analyzer',
  cyber_ransomware_trap: 'Cyber Ransomware Trap Scanner',
  cyber_quote_comparison: 'Cyber Quote Comparison',
  do_auto: 'D&O (Automatic)',
  do_benchmark: 'D&O Benchmark',
  do_renewal_delta: 'D&O Renewal Delta (YOY)',
  do_distress_scanner: 'D&O Financial Distress Scanner',
  do_emerging_exposure: 'D&O Emerging-Exposure Analyzer',
  home_auto: 'Home Insurance (Automatic)',
  home_owner_benchmark: 'HO Owner Benchmark',
  home_tenant_condo_benchmark: 'HO Tenant + Condo Benchmark',
  home_quote_comparison: 'HO Quote Comparison',
  home_renewal_delta: 'HO Renewal Delta (YOY)',
  home_dp_rental_benchmark: 'Dwelling Fire / Rental Benchmark',
  home_dp_rental_renewal_delta: 'Dwelling Fire / Rental Renewal Delta (YOY)',
  home_hnw_benchmark: 'HNW Homeowners Benchmark',
  plines_auto: 'Personal Lines (Automatic)',
  pa_benchmark: 'Personal Auto Benchmark',
  pa_quote_comparison: 'Personal Auto Quote Comparison',
  pa_renewal_delta: 'Personal Auto Renewal Delta (YOY)',
  umb_benchmark: 'Personal Umbrella Benchmark',
  umb_quote_comparison: 'Personal Umbrella Quote Comparison',
  umb_renewal_delta: 'Personal Umbrella Renewal Delta (YOY)',
  rt_auto: 'Requirements Consulting (Automatic)',
  rt_requirements_reviewer: 'Insurance Requirements Reviewer (RT-1R)',
  rt_conformance_alignment: 'Requirements Compliance Alignment (RT-3)',
  premdev_auto: 'Premium Development (Automatic)',
  premdev_workers_comp: 'Premium Dev — Workers Comp',
  premdev_prof_liability: 'Premium Dev — Professional Liab + D&O',
  premdev_general_liability: 'Premium Dev — General Liability',
  premdev_property: 'Premium Dev — Property',
  premdev_package_bop: 'Premium Dev — Package / BOP',
  lossrun_auto: 'Loss Runs (Automatic)',
  lossrun_fact_engine: 'Loss History Fact Engine (CLM-1)',
  lossrun_mitigation_wc: 'Loss Mitigation / UW Presentation — WC',
  re_auto: 'Real Estate (Automatic)',
  re_policy_extraction: 'RE Portfolio Coverage Extractor',
  re_risk_transfer: 'RE Contract Risk Transfer Analyzer',
  re_br_financials: "Builder's Risk Financials Extractor",
  re_zone_extractor: 'Procurement Zone-Value Extractor',
  re_portfolio_brief: 'RE Portfolio Board Brief',
  rt_compliance_verifier: 'Compliance Verifier (RT-2)',
  cl_auto: 'Commercial Lines (Automatic)',
  cl_gl_benchmark: 'GL Benchmark (CL-GL)',
  cl_property_benchmark: 'Commercial Property Benchmark',
  cl_commercial_auto_benchmark: 'Commercial Auto Benchmark',
  cl_inland_marine_benchmark: 'Inland Marine Benchmark',
  cl_excess_benchmark: 'Excess & Umbrella Benchmark',
  cl_package_benchmark: 'Commercial Package Benchmark',
  cl_bop_benchmark: 'BOP Benchmark',
  cl_nytpao_flow: 'NY Action-Over Flow Analysis',
  cl_package_quote_comparison: 'Package Quote Comparison',
  cl_bop_quote_comparison: 'BOP Quote Comparison',
  cl_xarch_quote_comparison: 'Cross-Architecture Quote Comparison (PKG↔BOP)',
  cl_package_renewal_delta: 'Package Renewal Delta (YOY)',
  cl_bop_renewal_delta: 'BOP Renewal Delta (YOY)',
  ca_auto: 'Coverage & Claims Analysis (Automatic)',
  ca_coverage_determination: 'Coverage Determination (CA-1)',
  ca_portfolio_determination: 'Portfolio Coverage Determination (CA-2)',
  ca_carrier_position_audit: 'Carrier Position Audit (CA-3)',
  ntr_auto: 'Notice Trigger Registers (Automatic)',
  ntr_first_party: 'Notice Trigger Register — First-Party & Specialty',
  ntr_property_liability: 'Notice Trigger Register — Property & Liability',
  ntr_mgmt_professional: 'Notice Trigger Register — Management & Professional',
};

export const CyberSuiteExecution: React.FC<{
  workflowType: string;
  documents: any[];
  additionalContext?: string;
  /** Selected past reports (e.g. a CLM-1 report for lossrun_mitigation_wc's standalone launch) — see `consumesReportOf`. */
  pastReports?: any[];
  /** Free-text workflowOptions convention ("audience=stewardship", "axes=AI,CRYPTO", etc.) — see WorkflowConfig.workflowOptions. */
  additionalInfo?: string;
  clientId: string;
  clientName?: string;
  projectId?: string;
  onComplete: (result: any) => void;
  onError?: (error: string) => void;
  onExecutionStart?: (workflowInfo: { workflowType: string; documents: any[] }) => void;
  onJobQueued?: (jobId: string) => void;
  token?: string | null;
  loading: boolean;
  reportTitle?: string;
  onReportTitleChange?: (title: string) => void;
}> = ({ workflowType, documents, additionalContext, pastReports, additionalInfo, clientId, clientName, projectId, onComplete, onError, onExecutionStart, onJobQueued, token, loading, reportTitle, onReportTitleChange }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Initializing...');
  const [error, setError] = useState<CategorizedError | null>(null);

  const displayName = CYBER_TOOL_DISPLAY_NAMES[workflowType] || workflowType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      const { progress: newProgress } = event.detail;
      setProgress(newProgress);
      setProgressMessage(getProgressMessage(newProgress));
    };
    window.addEventListener('cyberSuiteProgress' as any, handleProgressUpdate);
    return () => {
      window.removeEventListener('cyberSuiteProgress' as any, handleProgressUpdate);
    };
  }, []);

  const getProgressMessage = (progress: number): string => {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading documents...';
    if (progress < 40) return 'Reading policy against the benchmark standard...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 70) return 'AI analyzing coverage components... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations...';
    if (progress < 90) return 'Saving report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  };

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      setProgress(0);
      setProgressMessage(`Starting ${displayName}...`);
      setError(null);

      if (onExecutionStart) {
        onExecutionStart({ workflowType, documents });
      }

      const documentIds = documents?.map((d: any) => d.id) || [];

      devLog(`🔄 CyberSuiteExecution (${workflowType}): Starting analysis with:`, {
        documentsCount: documents?.length,
        additionalContext,
        clientId,
        projectId,
        documentIds
      });

      // Try to submit to job queue first (if token available) — matches the
      // background-job UX every other unified workflow uses.
      if (token && onJobQueued) {
        try {
          const jobResponse = await jobsService.submitJob(token, {
            workflowType,
            customerId: clientId,
            projectId: projectId,
            documents: documentIds,
            additionalContext: additionalContext,
            pastReports: pastReports?.map((r: any) => r.id) || undefined,
            additionalInfo: additionalInfo || undefined,
            reportTitle: reportTitle || undefined,
          });

          if (jobResponse.success) {
            devLog(`✅ CyberSuiteExecution: Job queued successfully: ${jobResponse.jobId}`);
            setIsAnalyzing(false);
            onJobQueued(jobResponse.jobId);
            return;
          }
        } catch (queueError) {
          devLog('⚠️ CyberSuiteExecution: Job queue not available, falling back to synchronous execution:', queueError);
        }
      }

      // FALLBACK: unified execute endpoint (self-polling, generic to any workflowType)
      const analysisResult = await workflowService.executeUnifiedWorkflow(
        workflowType as any,
        {
          documents,
          clientId,
          projectId,
          context: additionalContext,
          pastReports,
          additionalInfo,
        },
        (p) => {
          setProgress(p);
          setProgressMessage(getProgressMessage(p));
        }
      );

      setResult(analysisResult);
      setProgress(100);
      setProgressMessage('Complete!');
      onComplete(analysisResult);
    } catch (err) {
      console.error(`❌ CyberSuiteExecution (${workflowType}): Error analyzing documents:`, err);
      const categorized = categorizeError(err);
      setError(categorized);
      setProgressMessage(categorized.userMessage);
      if (onError) {
        onError(categorized.technicalMessage);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">{displayName}</h3>

      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <strong>Documents:</strong> {documents.length} document{documents.length !== 1 ? 's' : ''} selected
        </p>
      </div>

      {isAnalyzing && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">Performing {displayName}</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">{progressMessage}</p>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-sm text-blue-700 dark:text-blue-300 mb-2">
              <span>{progress}% complete</span>
              <span>{progress < 100 ? 'Processing...' : 'Done!'}</span>
            </div>
            <div className="bg-blue-200 dark:bg-blue-800 rounded-full h-3 overflow-hidden">
              <div className="bg-blue-500 dark:bg-blue-400 h-3 rounded-full transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      )}

      {error && !isAnalyzing && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-1">
              <h4 className="font-medium text-red-800 dark:text-red-200">{error.userMessage}</h4>
              {error.suggestedAction && <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error.suggestedAction}</p>}
              <button onClick={handleAnalyze} className="mt-3 px-4 py-2 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded-lg hover:bg-red-200 dark:hover:bg-red-700 text-sm font-medium">Retry Analysis</button>
            </div>
          </div>
        </div>
      )}

      {!isAnalyzing && !result && !error && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Report Title (Optional)</label>
            <input type="text" value={reportTitle || ''} onChange={(e) => onReportTitleChange?.(e.target.value)} placeholder="Custom report title (leave blank for default)" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" />
          </div>
          <div className="text-center">
            <button onClick={handleAnalyze} disabled={isAnalyzing} className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50">Execute {displayName}</button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">{displayName} Complete</h4>
          <p className="text-sm text-green-700 dark:text-green-300">{result.summary || `${displayName} completed successfully`}</p>
        </div>
      )}
    </div>
  );
};